// The detail sheet — what turns a triage loop into a planner (Phase 3.5).
//
// Tap anything you are holding and you can give it a date, make it repeat, take
// back a completion, put it on the Menu or let it go. Before this, the only
// thing the app could do to an item was route it six ways once, and the decay
// primitive had no path into it at all.
//
// One sheet, one item, every control a real <button> at full target size. It is
// a native <dialog>, so the platform gives us the modal semantics, Esc, and the
// focus trap rather than us reimplementing them badly.
//
// Only what is POSSIBLE for this item is shown: offering "Bring back from the
// Menu" for something that is not on the Menu would be a button that either does
// nothing or does something surprising, and this audience is exactly the one for
// whom a surprising control is expensive.

import type { Session } from './session.ts';
import type { AppEvent, MenuCategory } from '../events.ts';
import type { StampContext } from './session.ts';
import { isAppClock, noteOf, situationOf, weightOf, type NodeState } from '../fold.ts';
import { DEMAND_FREE_KINDS, type NodeKind } from '../events.ts';
import { kindWords, nodeWords } from '../kind-words.ts';
import { everyDaysWords, localDayKey, atMidnight, recordDayWords } from '../time.ts';
import { clockDayWords, contentsWords } from '../held.ts';
import { pressureOf, pressureWords } from '../pressure.ts';
import {
  isArrangement, dependsOnOthers, arrangementWords, confirmedDaysAgo,
  standsAt, changedBy,
} from '../arrangement.ts';
import {
  markArrangementEvents, unmarkArrangementEvents, setDependsEvents, clearDependsEvents,
  setStandsEvents, setChangesEvents,
} from './arrangement-intents.ts';
import {
  setDueEvents, clearDueEvents, makeRepeatEvents, stopRepeatEvents,
  undoneEvents, untrashEvents, promoteNodeFromMenuEvents, toMenuEvents, renameEvents,
  setStartEvents, clearStartEvents, estimateEvents, createParentEvents, cleanTitle,
  situationEvents, afterEvents, clearAfterEvents, releaseEvents, reclaimEvents, weightEvents,
  cleanNote, noteEvents, chooseTodayEvents, releaseTodayEvents,
} from './detail-intents.ts';
import { normalize } from '../search.ts';
import { doneEvents } from './work.ts';
import { declareFeedsEvents, releaseFeedsEvents, endOfDayKey } from './detail-intents.ts';
import { changeContainerKindEvents, makeContainerEvents, parentEvents, unparentEvents } from './detail-intents.ts';
import { biteEvents } from './work-intents.ts';
// The sorting pass's own event, so the sheet's second door writes the same act.
import { heatEvents } from './triage-intents.ts';
import { linkPersonEvents, closeWaitingEvents } from './detail-intents.ts';
import { attachContextEvents, detachContextEvents, attachRoleEvents, detachRoleEvents } from './detail-intents.ts';
import { allContexts, contextsOf } from '../contexts.ts';
// "home, office, text" is THREE places (3.8.0) — see `src/names.ts` for why the
// placeholder was kept and the behavior changed rather than the other way round.
import { splitNames, andWords } from '../names.ts';
import { allRoles, rolesOf, lineView, lineViewWords } from '../roles.ts';
import { servesNode } from '../serves.ts';
import { heldWork } from '../gate.ts';
import { setTrackRoleEvents, setSuspenseEvents } from './detail-intents.ts';
import { setSaveForEvents } from './detail-intents.ts';
import { people as peopleNodes, withWhom, openDays, waitingWords, isOpenWaiting } from '../people.ts';
import { dependencyView, dependencyWords, wouldCycle, feedCandidates } from '../dependencies.ts';
import {
  legalParents, childrenOf, placeWords, isContainer, CONTAINER_ORDER, CONTAINER_DEFAULT,
  containerOptionWords,
} from '../tree.ts';
import { eventWords, isCure } from '../log-words.ts';
import { choosable, chosenToday, composedFull, todayIsOn } from '../composed.ts';
import { canHold, legalMergeTargets, mergePlan, unmergeEvents } from './merge-intents.ts';
import { focusSheetTitle } from './sheets.ts';
import { decisionsFor, foldedIntoDeep } from '../merged.ts';
import { carryEvents, declineEvents, parkToSlotEvents } from './request-intents.ts';
import { nextSlotOccurrence, slotDayWords, slotOf, standingDecline } from '../requests.ts';
import { personView, stakeholdersOf, type PersonLine } from '../people.ts';
import { logDecisionEvents, removeStakeholderEvents, releasePromiseEvents, releaseHoldingEvents } from './detail-intents.ts';
import { rangeWords, timedRange } from '../duration.ts';
import { boundaryOf } from '../day.ts';

/** The relation words the sheet shows. The stored values are the vocabulary's
 *  closed set; these are what a person reads. */
const RELATION_WORDS: Record<string, string> = {
  'waiting-on': 'they owe me this',
  'promised-to': 'I said I would',
  'requested-by': 'they asked for it',
  'opr': 'they are running it',
  // The directory, in both directions (3.20.0, ADR-0122): where the rest of a
  // thing sits, stated as a fact about the thing and never as a deficit of
  // anybody. No surface may ever put an age beside either.
  'rest-with-them': 'they hold the rest of this',
  'rest-with-me': 'I hold the rest of this',
  'stakeholder': 'they care about it',
  'mentioned': 'they came up',
};

/** What a caller can ask the sheet to have OPEN when it arrives. A trip that
 *  lands on the folded half is a trip that failed (1.39.1), and this is the
 *  third surface to need saying so. */
export interface DetailOpen { reveal?: 'person' }
export interface DetailUI { open(node: NodeState, opts?: DetailOpen): void }

export function mountDetail(session: Session, now: () => number, onChange: () => void): DetailUI {
  /**
 * The day this sheet is rendering in — the person's, not everybody's.
 *
 * THIS WAS A REAL BUG AND IT WAS VISIBLE ON DAY ONE. The write path was threaded
 * in V2 stage 5, so a date set under a 3am boundary stores 02:59:59 the NEXT
 * morning — correct, and the whole point. The sheet then rendered that instant
 * back with `atMidnight`, so the date picker showed TOMORROW. Set a date for the
 * 9th, reopen the sheet, read the 10th.
 *
 * A surface that cannot show back what somebody just told it is worse than one
 * that never offered the control.
 */
const dayOf = (s: { zone: string; state: () => import('../fold.ts').State }): import('../time.ts').DayShape =>
  ({ zone: s.zone, boundary: boundaryOf(s.state()) });

const q = <T extends HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

/**
 * WHICH CLOCK THE DATE CONTROL OWNS — asked once, answered for both readers.
 *
 * 3.23.16 taught the FILL to read a container's `review` slot, because a
 * container dated through the sort flow writes `review` and the box was coming
 * up empty while the card above it said when the thing came back. It did not
 * teach `storedDue`, twelve hundred lines away, which went on reading `due`
 * alone.
 *
 * SO THE TWO COULD NEVER AGREE ON A CONTAINER, and "Not kept yet — press Set."
 * — the line whose entire job is to say the field and the store disagree —
 * showed for ever. A cold read photographed it between a header reading
 * *comes back Sep 20* and a footer reading *Comes back Sep 20.*, and it
 * survived pressing Set, which is precisely the moment it promises to go.
 *
 * That line exists because a picked day looks exactly like a kept one, and a
 * reader who cannot tell is a reader who believes something is scheduled when
 * it is not. A version of it that is ALWAYS on says nothing at all, and worse:
 * it trains somebody to ignore the one warning that matters.
 *
 * One function, two callers, so they cannot drift apart again — the shape
 * `whyCovered` and `heldWork` are each written once for.
 *
 * `isAppClock` stays load-bearing: every container carries the gate's own cure
 * in `review` from the moment it exists, and showing that would put a date
 * nobody set into the box on every container in the store.
 */
const ownDateClock = (n: NodeState | null | undefined) => {
  if (!n) return undefined;
  return isContainer(n)
    ? (isAppClock(n.clocks.review) ? undefined : n.clocks.review) ?? n.clocks.due
    : n.clocks.due;
};

  const dlg = q<HTMLDialogElement>('#detail');
  const title = q('#detail-title');
  const state = q('#detail-state');
  const date = q<HTMLInputElement>('#detail-date');
  const name = q<HTMLInputElement>('#detail-name');
  const every = q<HTMLInputElement>('#detail-every');
  const slack = q<HTMLInputElement>('#detail-slack');
  const live = q('#detail-live');
  const hint = q('#detail-repeat-hint');
  const feedsSel = q<HTMLSelectElement>('#detail-feeds');
  const leadInput = q<HTMLInputElement>('#detail-lead');
  const feedsList = q('#detail-feeds-list');
  const parentSel = q<HTMLSelectElement>('#detail-parent');
  const parentFilter = q<HTMLInputElement>('#detail-parent-filter');
  const AFTER = q<HTMLSelectElement>('#detail-after');
  const afterFilter = q<HTMLInputElement>('#detail-after-filter');
  const afterClear = q<HTMLButtonElement>('#detail-after-clear');
  const afterNow = q<HTMLElement>('#detail-after-now');
  const parentCreate = q<HTMLButtonElement>('#detail-parent-create');
  const parentNewRow = q<HTMLElement>('#detail-parent-new');
  const parentKind = q<HTMLSelectElement>('#detail-parent-kind');
  // The correction's select (3.23.31), beside the creation one. Two elements,
  // one vocabulary — both are filled by the same loop from `CONTAINER_ORDER`.
  const kindSel = q<HTMLSelectElement>('#detail-kind');
  const startInput = q<HTMLInputElement>('#detail-start');
  const estimateInput = q<HTMLInputElement>('#detail-estimate');
  const noteInput = q<HTMLTextAreaElement>('#detail-note');
  const situationInput = q<HTMLTextAreaElement>('#detail-situation');
  const standsInput = q<HTMLTextAreaElement>('#detail-stands');
  const changesInput = q<HTMLTextAreaElement>('#detail-changes');
  const mergeFilter = q<HTMLInputElement>('#detail-merge-filter');
  const mergeSel = q<HTMLSelectElement>('#detail-merge');
  const mergedList = q<HTMLElement>('#detail-merged-list');
  const personInput = q<HTMLInputElement>('#detail-person');
  const relationSel = q<HTMLSelectElement>('#detail-relation');
  const peoplePicks = q('#detail-people-picks');
  const peopleList = q('#detail-people-list');
  const stakeList = q('#detail-stakeholder-list');
  const decisionInput = q<HTMLTextAreaElement>('#detail-decision');
  const decisionList = q('#detail-decision-list');
  const decisionCount = q('#detail-decision-count');
  const placeLine = q('#detail-place');
  const holdingLine = q('#detail-holding');
  const kidsList = q('#detail-children');
  const personCount = q('#detail-person-count');
  const personOwes = q('#detail-person-owes');
  const personInvolves = q('#detail-person-involves');
  if (!dlg || !title || !state || !date || !name || !every || !slack || !live || !hint) {
    return { open() {} };
  }
  const NAME = name;
  const FEEDS = feedsSel, LEAD = leadInput, FEEDS_LIST = feedsList;
  const PARENT = parentSel, PLACE = placeLine, KIDS = kidsList;
  const PERSON = personInput, RELATION = relationSel, PEOPLE = peoplePicks, PEOPLE_LIST = peopleList;
  const DLG = dlg, TITLE = title, STATE = state, DATE = date, EVERY = every, SLACK = slack, LIVE = live;

  let current: NodeState | null = null;
  let busy = false;
  // Whether the place and role picks are showing their REMOVAL words (3.8.0).
  // Device state and never an event: which words this sheet is currently
  // wearing is not a fact about anybody's work, and law 7 keeps the log to
  // facts about the work.
  let fixingPlaces = false;
  let fixingRoles = false;

  const btn = (sel: string): HTMLButtonElement | null => q<HTMLButtonElement>(sel);

  // --- a picked day is not a saved day (1.38.2) --------------------------------
  //
  // Found on a real device: the native picker fills a `<input type="date">` the
  // instant a day is chosen, and a filled field looks exactly like a kept one.
  // The Set button beside it is the only thing that writes, and nothing said so
  // — so a date could be chosen, believed, and simply not be there.
  //
  // That is the worst shape a defect can take in this app. Everything here is
  // built so you do not have to hold things in your head; a control that lets
  // you believe something is scheduled when it is not takes that back with
  // interest, and it does it quietly.
  //
  // NOT a warning, and deliberately not styled as one. Nothing is wrong while
  // you are still deciding — this only names the difference between a field
  // holding a proposal and a field holding a fact. It disappears the moment the
  // two agree, so it can never nag about a date that IS set.
  const unsaved = (inputSel: string, lineSel: string, stored: () => string): () => void => {
    const field = q<HTMLInputElement>(inputSel);
    const line = q(lineSel);
    const paint = (): void => {
      if (!field || !line) return;
      const differs = field.value !== stored();
      line.hidden = !differs;
      line.textContent = differs ? 'Not kept yet — press Set.' : '';
    };
    field?.addEventListener('input', paint);
    field?.addEventListener('change', paint);
    return paint;
  };


  /**
   * The parent picker, painted against the filter box (1.3.0). At 45 imported
   * projects a bare select was a scroll test; typing narrows it, each option
   * names where IT sits ("Boy Scouts — in Volunteering") so same-named places
   * stay tellable apart, and when the typed words name no existing container
   * the create button offers to make the project AND file this under it in one
   * gated commit.
   */
  function paintParents(n: NodeState): void {
    if (!PARENT) return;
    const st = session.state();
    const legal = legalParents(st, n);
    const query = normalize(parentFilter?.value ?? '');
    const shown = query
      ? legal.filter(t => normalize(t.title || '').includes(query))
      : legal;
    const keep = PARENT.value;
    // ONE writer for these words — see `containerOptionWords`, which replaced
    // three identical copies, two of them in this file. They said the same
    // thing in the same words by coincidence rather than by construction, which
    // is the shape this repo keeps paying for: one concept, several places, and
    // only one of them gets the fix. It names the KIND too, which stopped being
    // optional the moment more than one kind could appear in a place picker.
    const lineage = (t: NodeState): string => containerOptionWords(st, t);
    PARENT.replaceChildren(...[
      Object.assign(document.createElement('option'), {
        value: '',
        // The empty option's words change with what is actually possible. A
        // fixed "pick one" over an empty list tells someone to do something
        // the app cannot let them do yet.
        textContent: legal.length === 0 ? 'nothing to put it under yet'
          : shown.length === 0 ? 'nothing matches that'
            : 'pick something',
      }),
      ...shown.map(t => Object.assign(document.createElement('option'), {
        value: t.id, textContent: lineage(t),
      })),
    ]);
    if (shown.some(t => t.id === keep)) PARENT.value = keep;
    PARENT.disabled = legal.length === 0;

    // Offer creation only when the typed words are a usable title that names no
    // existing container — an exact match means the place already exists and
    // the offer would mint a duplicate.
    if (parentCreate) {
      const raw = parentFilter?.value ?? '';
      const clean = cleanTitle(raw);
      // Over ALL live containers, not the LEGAL list: legalParents excludes the
      // node's CURRENT parent, so typing its title showed "nothing matches
      // that" plus an offer to mint an empty doppelganger and silently re-home
      // into it (audit). A place that exists is never offered as new.
      const exact = clean !== '' && [...st.nodes.values()]
        .filter(t => !t.trashed && !t.mergedInto && isContainer(t))
        .some(t => normalize(t.title || '') === normalize(clean));
      const offer = clean !== '' && !exact;
      parentCreate.hidden = !offer;
      if (parentNewRow) parentNewRow.hidden = !offer;
      if (offer) {
        // The button says what the picker is set to, so the two cannot disagree
        // on screen — a control reading "New project" while the select says
        // Goal is a receipt for something that is not about to happen.
        const chosen = (parentKind?.value ?? CONTAINER_DEFAULT) as NodeKind;
        const word = CONTAINER_ORDER.find(([k]) => k === chosen)?.[1] ?? 'Project';
        parentCreate.textContent =
          `New ${word.split(' — ')[0]!.toLowerCase()} named “${clean}” — put it under that`;
      }
    }
  }

  /**
   * WHAT THIS WAITS FOR (1.30.0). The parent picker's manners, and the write
   * gate's rules mirrored into what is OFFERED — a control that offers a choice
   * the gate then refuses is a control that lies about what it does.
   *
   * So the list excludes: this node itself; anything already finished, trashed
   * or merged away; every demand-free kind, which is never "finished" and so
   * could never fire the cue; and anything that already waits, directly or at
   * any remove, on this node — that is the loop, and it is walked rather than
   * checked one step deep, because A after B after C after A is the shape that
   * gets written by somebody building a routine out of order.
   */
  function paintAfter(n: NodeState): void {
    if (!AFTER) return;
    const st = session.state();
    // Does `cand` already wait on `n`, at any remove? The walk guards itself, so
    // a loop already in the store terminates rather than hanging the render.
    const waitsOnThis = (cand: NodeState): boolean => {
      const walked = new Set<string>();
      let cur: string | null = cand.id;
      while (cur && !walked.has(cur)) {
        if (cur === n.id) return true;
        walked.add(cur);
        cur = st.nodes.get(cur)?.after ?? null;
      }
      return false;
    };
    const legal = [...st.nodes.values()]
      .filter(t => t.id !== n.id && !t.trashed && !t.mergedInto && !t.lastDone)
      .filter(t => !(DEMAND_FREE_KINDS as readonly string[]).includes(t.kind))
      // A resume card is a way back into a thread, not a step anything queues
      // behind, and it spends itself the moment it is picked up.
      .filter(t => t.kind !== 'resume-card')
      .filter(t => !waitsOnThis(t))
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    const query = normalize(afterFilter?.value ?? '');
    const shown = query ? legal.filter(t => normalize(t.title || '').includes(query)) : legal;
    const keep = AFTER.value;
    AFTER.replaceChildren(...[
      Object.assign(document.createElement('option'), {
        value: '',
        textContent: legal.length === 0 ? 'nothing here it could wait for'
          : shown.length === 0 ? 'nothing matches that'
            : 'pick something',
      }),
      ...shown.map(t => Object.assign(document.createElement('option'), {
        value: t.id, textContent: t.title || '(untitled)',
      })),
    ]);
    if (shown.some(t => t.id === keep)) AFTER.value = keep;
    AFTER.disabled = legal.length === 0;

    // WHAT IT IS WAITING FOR NOW, said plainly — including when the thing it
    // waits for has since been finished or let go. A standing state the reader
    // cannot see is the same defect as a clock nobody reads.
    const a = n.after ? st.nodes.get(n.after) : undefined;
    if (afterNow) {
      const words = !n.after ? ''
        : !a ? 'It was waiting for something that is no longer here, so it has come back to you.'
          : a.trashed || a.mergedInto ? `“${a.title || '(untitled)'}” was let go, so this has come back to you.`
            : a.lastDone ? `“${a.title || '(untitled)'}” is done — this is back with you now.`
              : `Waiting for “${a.title || '(untitled)'}”.`;
      afterNow.textContent = words;
      afterNow.hidden = words === '';
    }
    if (afterClear) afterClear.hidden = !n.after;
  }

  /** The fold-into picker (1.7.0): the parent picker's manners — narrowed as
   *  you type, lineage named, the empty option honest about what is possible.
   *  Only legal targets are offered (never itself, never its own descendant,
   *  people only into people) — the recorded never-offer-then-refuse rule. */
  function paintMergeTargets(n: NodeState): void {
    if (!mergeSel) return;
    const st = session.state();
    const query = normalize(mergeFilter?.value ?? '');
    const legal = legalMergeTargets(st, n);
    const shown = query ? legal.filter(t => normalize(t.title || '').includes(query)) : legal;
    // ONE writer for these words — see `containerOptionWords`, which replaced
    // three identical copies, two of them in this file. They said the same
    // thing in the same words by coincidence rather than by construction, which
    // is the shape this repo keeps paying for: one concept, several places, and
    // only one of them gets the fix. It names the KIND too, which stopped being
    // optional the moment more than one kind could appear in a place picker.
    const lineage = (t: NodeState): string => containerOptionWords(st, t);
    const keep = mergeSel.value;
    mergeSel.replaceChildren(...[
      Object.assign(document.createElement('option'), {
        value: '',
        textContent: legal.length === 0 ? 'nothing else to fold into'
          : shown.length === 0 ? 'nothing matches that' : 'pick the one that stays',
      }),
      ...shown.map(t => Object.assign(document.createElement('option'), {
        value: t.id, textContent: lineage(t),
      })),
    ]);
    if (shown.some(t => t.id === keep)) mergeSel.value = keep;
    mergeSel.disabled = legal.length === 0;
  }

  /** Who cares how it goes, and what was decided (1.9.0, ADR-0057).
   *  Stakeholders come from `people[]` — the one home — so a link written
   *  any time since 0.15.0 appears with nothing re-entered. A decision row
   *  carries NO verb: the log is read-only, and the way back is to log the
   *  new decision. */
  const DECISIONS_SHOWN = 5;
  function paintMeeting(n: NodeState): void {
    const st = session.state();
    if (stakeList) {
      stakeList.replaceChildren(...stakeholdersOf(st, n).map(p => {
        const li = document.createElement('li');
        li.className = 'detail-feed';
        const label = document.createElement('span');
        const who = p.title || '(unnamed)';
        label.textContent = who;
        const off = document.createElement('button');
        off.type = 'button';
        off.className = 'ghost';
        off.textContent = 'Take them off';
        off.setAttribute('aria-label', `Take them off the list — ${who}`);
        off.addEventListener('click', () => {
          void run(ctx => removeStakeholderEvents(ctx, n.id, p.id),
            'Off the list. The record keeps that they were on it.');
        });
        li.append(label, off);
        return li;
      }));
    }
    // Through the fold (1.9.2): what was decided about this AND about
    // everything folded into it. Until then a fold silently took the source's
    // decision log off every surface. `decisionsFor` owns the total order.
    const rows = decisionsFor(st, n);
    if (decisionCount) {
      decisionCount.textContent = rows.length === 0 ? ''
        : rows.length === 1 ? 'One decision, kept.'
          : rows.length <= DECISIONS_SHOWN ? `${rows.length} decisions, newest first.`
            : `${rows.length} decisions — the ${DECISIONS_SHOWN} most recent are shown.`;
    }
    if (decisionList) {
      decisionList.replaceChildren(...rows.slice(0, DECISIONS_SHOWN).map(d => {
        const li = document.createElement('li');
        li.className = 'detail-feed';
        const text = document.createElement('span');
        text.textContent = d.text;
        const when = document.createElement('span');
        when.className = 'detail-when';
        // THE READER'S LOCALE (3.23.8), like `recordDayWords` and
        // `capture-context.ts`. The third and last hardcoded 'en-GB'.
        const day = new Intl.DateTimeFormat(undefined, {
          timeZone: session.zone, day: 'numeric', month: 'short',
        }).format(new Date(d.at));
        // Attributed when it was decided about something folded in — the
        // sheet already lists what folded in, so an unattributed row would
        // invite reconciling two lists that do not line up.
        const from = d.from ? st.nodes.get(d.from)?.title || '(untitled)' : null;
        when.textContent = [day, d.meeting, from ? `from ${from}` : null]
          .filter(Boolean).join(' · ');
        li.append(text, when);
        return li;
      }));
    }
  }

  /** Someone asked (1.8.0, ADR-0056): the fact line, the decline, the way
   *  back, and the slot button naming the REAL day it would come back —
   *  a control that says what it will do (the composed-cap rule). */
  function paintRequest(n: NodeState): void {
    const st = session.state();
    const fact = q<HTMLElement>('#detail-request-fact');
    const asked = [...n.people].reverse().find(p => p.relation === 'requested-by');
    if (fact) {
      const who = asked ? (st.nodes.get(asked.person)?.title || null) : null;
      fact.hidden = !who;
      if (who) fact.textContent = `${who} asked for this.`;
    }
    // `standingDecline`, not raw `n.notNow` (1.17.4): the record now survives
    // completion in state, and a completed thing must not read as declined
    // here while the ledger — the same predicate — shows no row.
    const standing = standingDecline(n);
    const declined = standing !== null;
    const declineBtn = btn('#detail-decline');
    if (declineBtn) declineBtn.hidden = declined;
    const declinedBox = q<HTMLElement>('#detail-declined');
    if (declinedBox) declinedBox.hidden = !declined;
    const words = q<HTMLElement>('#detail-declined-words');
    if (words && standing) {
      // THE SAME FACT, THE SAME WORDS. `requests.ts` renders this ledger entry
      // with `recordDayWords` — "3 Aug" — and this one printed the key, so the
      // Not Now ledger and a thing's own page described one decline two ways.
      const day = recordDayWords(standing.at, session.zone, new Date(now()).toISOString());
      const who = standing.person ? (st.nodes.get(standing.person)?.title || null) : null;
      words.textContent = who
        ? `Declined ${day} — ${who} asked. It sits in the Not Now ledger.`
        : `Declined ${day}. It sits in the Not Now ledger.`;
    }
    const slotBtn = btn('#detail-slot-park');
    if (slotBtn) {
      const day = slotOf(st);
      const offer = !declined && day !== null && !n.clocks.park;
      slotBtn.hidden = !offer;
      if (offer && day) {
        const back = localDayKey(
          nextSlotOccurrence(day, new Date(now()).toISOString(), session.zone), dayOf(session));
        slotBtn.textContent = `Park it until the request slot — back ${back} (${slotDayWords(day)})`;
      }
    }
  }

  /**
   * Say it where it can be seen AND where it can be heard. A failure reported
   * only to a visually-hidden region is a failure a sighted user never learns
   * about (F-08).
   *
   * ONE PLACE NOW, BECAUSE THE MIRROR NEVER WORKED (3.23.8). F-08's remedy was
   * to write the message into `STATE` as well — but `STATE` is the fact line,
   * and `render` rewrites it from `bits` on every paint. `run` renders straight
   * after a write, so a failure mirrored there survived microseconds. Only the
   * validation messages that return BEFORE `run` ever stayed on screen, which
   * is why it looked fixed.
   *
   * `#detail-live` is visible instead, pinned above Close where nothing scrolls
   * it away and nothing repaints it. That covers what F-08 asked for and the
   * half nobody had noticed was missing: the SUCCESS path, which wrote to the
   * hidden region alone and told a sighted reader nothing at all.
   */
  const say = (msg: string): void => { LIVE.textContent = msg; };

  /** Commit, then re-read the node from fresh state — never from the stale copy
   *  the sheet was opened with, which would render yesterday's answer. */
  // `announce` may be a thunk so a handler can say what the batch actually did
  // — the merge names an edge that could not come across (1.9.2).
  const run = async (
    make: Parameters<Session['commit']>[0], announce: string | (() => string),
  ): Promise<void> => {
    if (!current || busy) return;
    busy = true;
    const id = current.id;
    try {
      await session.commit(make);
      LIVE.textContent = typeof announce === 'function' ? announce() : announce;
    } catch (err) {
      say(`Couldn’t do that — ${(err as Error).message}`);
    } finally {
      busy = false;
    }
    try { onChange(); } catch { /* a render bug must not contradict a landed write */ }
    const fresh = session.state().nodes.get(id);
    if (fresh) render(fresh);
  };

  /** Re-read this item from live state and repaint the sheet from it.
   *
   *  The rule `run` already follows, lifted out because the picks' toggle needs
   *  it too: a handler bound at paint time can be pressed much later, and
   *  rendering the copy it closed over would put a stale sheet on screen. */
  const repaint = (): void => {
    if (!current) return;
    const fresh = session.state().nodes.get(current.id);
    if (fresh) render(fresh);
  };

  /**
   * WHAT YOU ALREADY HAVE, AS TAPS — AND THE WAY TO TAKE A WRONG ONE OUT (3.8.0).
   *
   * This replaces a native `<datalist>` on all three of this sheet's naming
   * fields, and the reason it is gone is in the markup beside the first one.
   * The short of it: the browser's popup took the caret out of the field
   * mid-word, repeatedly, on the device this app is used on — and it was the
   * one thing on any surface here that could not be styled, sized, ringed or
   * measured, which is what `tools/a11y.mjs` exists to make impossible.
   *
   * ONE FUNCTION FOR THREE FIELDS. Places, roles and people are the same shape
   * on purpose — each group's own note says so — and three copies of this would
   * be three places for the next fix to reach two of.
   *
   * `drop` is the second half, and only the two label axes get it. A place is a
   * thing somebody typed into existence, so a place can be typed wrong; saying
   * one is not a place has worked since 2.34.0 and lived exclusively in the
   * situation sheet, behind first choosing that label as where you are. Two
   * rooms from the field that makes the mistake. Pressed, the SAME picks change
   * their words and remove instead of adding — no control ever means two things
   * at once, because what changed is what it says.
   *
   * A person is not that: nobody mistypes a human into being, and the sheet
   * already carries the row that unlinks one. So people get picks and no toggle.
   */
  const paintPicks = (
    listEl: HTMLElement | null,
    all: readonly NodeState[],
    already: readonly string[],
    add: (n: NodeState) => void,
    drop?: {
      fixEl: HTMLElement | null;
      on: boolean;
      set: (v: boolean) => void;
      noun: string;
      remove: (n: NodeState) => void;
    },
  ): void => {
    if (!listEl) return;
    const fixing = drop?.on ?? false;
    // Adding shows what is NOT on this thing — offering to add what is already
    // there is a control that does nothing. Correcting shows every one of them,
    // because the label you typed wrong is usually the one you attached.
    const shown = fixing ? all : all.filter(c => !already.includes(c.id));
    // AN OFFER THAT SAYS IT IS ONE (3.23.14, cold read). Under the label "Where
    // can this be done?" an item with NO place showed a bare underlined "on the
    // phone", and it was read as the answer — as a statement about the item —
    // rather than as one of the places you could tap to add. Nothing on the
    // screen distinguished the two: the list has no name of its own, it sits
    // directly under the question, and every entry is styled like the links
    // elsewhere on this sheet that state facts.
    //
    // The lead is also the list's ACCESSIBLE NAME, so the same sentence reaches
    // a screen reader as the name of the region the buttons are inside, rather
    // than a run of unexplained place names after a question. It comes off
    // `drop.noun`, which both call sites already pass, so a third list gets it
    // without anybody remembering to write one.
    const noun = drop?.noun ?? 'one';
    const lead = fixing
      ? `Tap one to say it is not a ${noun}`
      : `${noun[0]!.toUpperCase()}${noun.slice(1)}s you already use — tap to add`;
    listEl.setAttribute('aria-label', lead);
    const leadEl = document.createElement('li');
    // `.detail-hint` VERBATIM for the ink, and a layout-only modifier beside it.
    // Same ink, same paper, so the contrast registry's existing row covers this
    // by construction rather than by a new entry somebody has to add (the shape
    // ACCESSIBILITY.md B-40 records, and hub LESSONS §28's same-commit rule).
    leadEl.className = 'detail-hint detail-picks-lead';
    leadEl.textContent = lead;
    listEl.replaceChildren(leadEl, ...shown.map(c => {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'linklike';
      const name = c.title || '(unnamed)';
      b.textContent = fixing && drop ? `${name} — not a ${drop.noun}` : name;
      b.addEventListener('click', () => {
        const fresh = session.state().nodes.get(c.id);
        if (!fresh) return;
        if (fixing && drop) drop.remove(fresh);
        else add(fresh);
      });
      li.append(b);
      return li;
    }));
    // Nothing to offer is nothing to introduce — an empty list under a lead-in
    // reads as a list that failed to load.
    if (shown.length === 0) listEl.replaceChildren();
    if (!drop?.fixEl) return;
    // Nothing to correct when there is nothing there, and the row goes with the
    // list rather than standing on an empty sheet offering to fix it.
    if (all.length === 0) { drop.fixEl.replaceChildren(); return; }
    const t = document.createElement('button');
    t.type = 'button';
    t.className = 'linklike';
    t.textContent = fixing ? 'Never mind' : `Something here is not a ${drop.noun}`;
    t.addEventListener('click', () => { drop.set(!fixing); repaint(); });
    drop.fixEl.replaceChildren(t);
  };


  function render(n: NodeState): void {
    const changed = current?.id !== n.id;
    current = n;
    // A new item opens in the shape every item opens in.
    if (changed) setRest(false);
    TITLE.textContent = n.title || '(untitled)';

    // What is true about it now, in words — never a color, never a badge.
    const p = pressureOf(n, new Date(now()).toISOString(), dayOf(session));
    const bits: string[] = [];
    // WHAT IT IS, FIRST (2.4.0, ADR-0094). This line said everything true about
    // a thing except the one fact that decides how to read the rest of it — a
    // goal and an action carrying the same clock mean different things, and the
    // sheet named neither. Null for `action`, so the common case is unchanged.
    const what = nodeWords(n);
    if (what) bits.push(what);
    if (n.trashed) bits.push('let go');
    if (n.mergedInto) {
      const survivor = session.state().nodes.get(n.mergedInto);
      bits.push(`folded into ${survivor?.title ? `“${survivor.title}”` : 'another thing'}`);
    }
    if (n.onMenu) bits.push('on the Menu');
    if (n.lastDone) bits.push('done');
    // WHAT YOU SAID, IN THE PHRASE THE OFFER CARD ALREADY USES (`nextup.ts`:120
    // — "you said it was hot"). Without it the two buttons below would be a
    // control with no state: you could change the answer and never find out
    // what it had been. Nothing is said when nobody answered, because
    // unanswered sits between hot and cold and is not a third verdict.
    if (n.heat) bits.push(`you said it was ${n.heat}`);
    // A CADENCE IS NOT A KIND. This read `kind === 'upkeep'`, so a goal or an
    // area carrying a rhythm said nothing about it here — the one line in the
    // sheet whose job is to tell you what this thing currently is.
    if (n.intervalDays) {
      bits.push(isContainer(n)
        ? `comes back ${everyDaysWords(n.intervalDays)}`
        : `repeats ${everyDaysWords(n.intervalDays)}`);
    }
    // The quiet fact line (1.4.0): where a sorted thing went, in the sorting's
    // own words — the sheet is where "it feels lost" gets its answer.
    // IN THE SORTING'S OWN WORDS, and one of them was not. Every route value
    // doubles as the label on the button that set it — Do now, Next action,
    // Waiting for, Someday, Reference — except `filed`, which no control has
    // ever shown anybody. A reader who pressed *Put it under something* was
    // told their thing was "sorted as filed", a word from the event vocabulary
    // wearing the line whose stated job is the reader's vocabulary.
    //
    // AND ONE FACT IS SAID ONCE (3.23.14). Two routes were answering the same
    // question a second time in a second register. A thing routed `waiting-for`
    // carries the kind word "Waiting for" six bits above and read
    // "Waiting for · sorted as waiting for"; a thing routed `someday` sits on
    // the Menu and read "on the Menu · sorted as someday". The reader has to
    // work out that the two halves are one fact, and the second half is the
    // machine's spelling of the first.
    //
    // DROPPED ON THE BIT, not on the route. Each route names the bit that would
    // already have said it, and the line goes only when that bit is actually
    // there — so a someday thing TAKEN OFF the Menu still says how it was
    // sorted, which is the case where this line earns its keep.
    if (n.route && n.route !== 'trash') {
      const said = n.route === 'filed'
        ? 'put under something'
        : String(n.route).replace(/-/g, ' ');
      const alreadySaidBy: Record<string, string | null> = {
        'waiting-for': kindWords('waiting-for'),
        someday: 'on the Menu',
      };
      const dup = alreadySaidBy[String(n.route)];
      if (!dup || !bits.includes(dup)) bits.push(`sorted as ${said}`);
    }
    if (standingDecline(n)) bits.push('in the Not Now ledger');
    if (isArrangement(n)) {
      // The words say CONFIRMED rather than done, because that is the whole
      // difference between an arrangement and the upkeep it is built on.
      const d = confirmedDaysAgo(n, new Date(now()).toISOString(), session.zone);
      bits.push(dependsOnOthers(n) ? 'runs itself, via someone else' : 'runs itself');
      bits.push(d === null ? 'never confirmed'
        : d === 0 ? 'confirmed today' : d === 1 ? 'confirmed yesterday' : `confirmed ${d} days ago`);
    }
    const words = pressureWords(p);
    if (words) bits.push(words);
    // A CLOCK THAT IS KEPT IS NOT A CLOCK THAT WILL SPEAK. The log is
    // append-only, so finishing a thing does not erase the day it carried —
    // and this line read that retained day out as `comes back`, on a thing the
    // same line had already called `done`. One row of the sheet said the work
    // was finished and the next said it was returning in October.
    //
    // An upkeep is the case where both ARE true: it is done AND it comes back,
    // which is what a cadence means, so the interval is what separates them
    // rather than the word `done`.
    // IN THE APP'S OWN WORDS FOR A DAY. This printed the raw key, so a thing's
    // own page said `comes back 2026-09-09` while the held card and the
    // coverage sheet both said *tomorrow* about that same date — three screens,
    // one date, two vocabularies, and the machine-shaped one on the page a
    // reader opens to understand a single thing.
    //
    // AND THE MENU IS ASKED FIRST. Routing to Someday clears every DEMAND clock
    // — due, start, suspense, park — and deliberately leaves the `review` clock
    // the gate wrote when the thing was captured, because a review clock is the
    // app's own resurfacing marker and was never a demand (triage-intents.ts's
    // DEMAND_KINDS says so, and gate.ts's law-6 belts pass it correctly). This
    // line then fell through `?? n.clocks.review` and read that bookkeeping
    // clock out as `comes back today`, one bit after `on the Menu`, to a reader
    // who had just been told the Menu has no clock. `heldGroups` has always had
    // the right precedence — it buckets `onMenu` BEFORE it looks at any clock —
    // so this is that order, here.
    const clock = n.clocks.due ?? n.clocks.review ?? n.clocks.start;
    const willReturn = !n.onMenu && (!n.lastDone || Boolean(n.intervalDays));
    if (clock && willReturn) {
      bits.push(`comes back ${clockDayWords(
        clock.at, new Date(now()).toISOString(), session.zone, dayOf(session))}`);
    }
    // A PERSON'S PLACES, IN A PERSON'S WORDS (3.21.0, ADR-0123). The places
    // group has always rendered on every kind, so places could be put on a
    // person before anything read them — the affiliation the choosers now
    // sort by. On a person the work-words are wrong, so the label swaps.
    {
      const ctxLabel = q<HTMLElement>('label[for="detail-context"]');
      const ctxHint = q<HTMLElement>('#detail-context-hint');
      if (ctxLabel) {
        ctxLabel.textContent = n.kind === 'person'
          ? 'Their places' : 'Where can this be done?';
      }
      if (ctxHint) {
        ctxHint.textContent = n.kind === 'person'
          ? 'Name a place and they are offered when you are there. No places means offered everywhere.'
          : 'As many as fit, commas between them, and none is required. With no place of its own, a thing goes where it lives — or anywhere, if that has none.';
      }
    }
    // AN IDENTITY SAYS WHAT IT IS (3.20.4, device report: a role's sheet
    // "looks like a task except for the little role label"). The kind word was
    // already first; these add the sentence that stops the task-chrome around
    // it reading as an error. A person's sheet already explains itself.
    if (n.kind === 'role') bits.push('a part of your life that work can belong to');
    if (n.kind === 'context') bits.push('a place where things can be done');
    STATE.textContent = bits.length ? bits.join(' · ') : 'held';

    // Seed the date box with the date it already has, so "Set" is an edit rather
    // than a blank slate you have to re-derive.
    // Do NOT clobber something the user is part-way through typing. `render` runs
    // after every commit in this sheet, so setting a date used to silently eat an
    // in-progress rename — in an app whose capture line persists a draft per
    // keystroke precisely because interruption is the expected case (audit).
    if (document.activeElement !== NAME || NAME.value.trim() === '') NAME.value = n.title;
    // THE CLOCK THIS THING ACTUALLY CARRIES (3.23.16). This read the `due` slot
    // only, so a container dated through the sort flow — which writes `review`,
    // for the reasons `triage-intents.ts` sets out — showed an EMPTY box while
    // the card two inches above it said "comes back in 6 days".
    //
    // `isAppClock` is what makes reading `review` safe: every container carries
    // the gate's own `gate:node.created` cure in that slot from the moment it
    // exists, and showing that as the reader's date would put a date nobody set
    // into the box on every container in the store — a worse defect than the one
    // being fixed, and in the direction this app must never round.
    //
    // `due` is still read as a fallback so a container dated by THIS control
    // before today keeps showing its date. That clock stays in the log and still
    // groups the node, so nothing is lost by the control no longer writing it.
    const own = ownDateClock(n);
    DATE.value = own ? localDayKey(own.at, dayOf(session)) : '';
    if (startInput) startInput.value = n.clocks.start ? localDayKey(n.clocks.start.at, dayOf(session)) : '';
    // The note rides the same no-clobber rule as the rename box: `render` runs
    // after every commit here, and prose is the costliest thing to eat.
    if (noteInput && document.activeElement !== noteInput) noteInput.value = noteOf(n) ?? '';
    // THE SAME FOCUS GUARD THE NOTE TAKES (3.18.0). A repaint that overwrote a
    // box somebody is typing in would lose the sentence they were halfway
    // through, and this sheet repaints on every render.
    if (standsInput && document.activeElement !== standsInput) {
      standsInput.value = standsAt(n) ?? '';
    }
    if (changesInput && document.activeElement !== changesInput) {
      changesInput.value = changedBy(n) ?? '';
    }
    // Same in-progress-edit guard as the note: repainting under a cursor throws
    // away what somebody is halfway through typing.
    if (situationInput && document.activeElement !== situationInput) situationInput.value = situationOf(n) ?? '';
    // The fields have just been set from the node, so nothing differs — this is
    // what CLEARS the line after a successful Set, since `render` runs on commit.
    refreshUnsaved();
    // The decision box obeys the same rule for the same reason — `render`
    // runs after every commit here, and prose is the costliest thing to eat.
    // It is cleared explicitly on a successful log, never by a repaint.
    if (n.intervalDays && n.intervalDays > 0) EVERY.value = String(n.intervalDays);
    if (n.comfortWindowDays && n.comfortWindowDays > 0) SLACK.value = String(n.comfortWindowDays);

    // Who it is with (the person lens's write side).
    // WHERE THIS CAN BE DONE (2.2.0, ADR-0092). The list is the removal control:
    // each place is a button that takes itself off, which is the shape the feeds
    // list already uses. No confirm — detaching a label loses nothing, and the
    // event is append-only so the log still says it was there.
    {
      const cst = session.state();
      const cList = q('#detail-context-list');
      paintPicks(
        q('#detail-context-picks'), allContexts(cst), n.contexts,
        c => { void run(ctx => attachContextEvents(ctx, n.id, c.id),
          `Can be done ${c.title || 'there'}.`); },
        {
          fixEl: q('#detail-context-fix'),
          on: fixingPlaces,
          set: (v) => { fixingPlaces = v; },
          noun: 'place',
          // The situation sheet's own words for the same act, because it is the
          // same act: the label stops being offered as somewhere you can be, and
          // stops narrowing the surface for anything still carrying it.
          remove: c => { void run(ctx => releaseEvents(ctx, c.id),
            `${c.title || 'That'} is not a place. It stops being offered, and stops hiding anything.`); },
        },
      );
      if (cList) {
        cList.replaceChildren(...contextsOf(cst, n).map(c => {
          const li = document.createElement('li');
          li.className = 'detail-feed';
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'linklike';
          b.textContent = `${c.title || '(unnamed)'} — take it off`;
          b.addEventListener('click', () => {
            void run(ctx => detachContextEvents(ctx, n.id, c.id),
              `No longer ${c.title || 'there'}.`);
          });
          li.append(b);
          return li;
        }));
      }
    }

    // WHO THIS IS FOR (2.6.0, ADR-0096). The block above, on the other axis —
    // identical by design rather than by accident: they are one shape, and a
    // gratuitous difference would be two mechanisms wearing one idea.
    {
      const rst = session.state();
      const rList = q('#detail-role-list');
      paintPicks(
        q('#detail-role-picks'), allRoles(rst), n.roles,
        r => { void run(ctx => attachRoleEvents(ctx, n.id, r.id),
          `Part of ${r.title || 'that'}.`); },
        {
          fixEl: q('#detail-role-fix'),
          on: fixingRoles,
          set: (v) => { fixingRoles = v; },
          noun: 'role',
          remove: r => { void run(ctx => releaseEvents(ctx, r.id),
            `${r.title || 'That'} is not a role. It stops being offered.`); },
        },
      );
      if (rList) {
        rList.replaceChildren(...rolesOf(rst, n).map(r => {
          const li = document.createElement('li');
          li.className = 'detail-feed';
          // THE NAME IS A DOOR NOW, AND TAKING IT OFF IS ITS OWN CONTROL
          // (3.12.0, ADR-0115). One button read `Name — take it off`, so from a
          // thing the only thing you could do with the line it is on was remove
          // it. The person row three hundred lines up has had a door since
          // 1.12.0 — tapping a name opens their sheet — and roles never got one,
          // which is the forward-only asymmetry in miniature: the app knew what
          // this thing was for and gave you no way to go and look.
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'linklike';
          b.textContent = r.title || '(unnamed)';
          b.addEventListener('click', () => {
            const line = session.state().nodes.get(r.id);
            if (!line) return;
            showNode(line);
            // Same reason the person door does this: a role's sheet is almost
            // entirely the folded half, and landing on it folded shut puts the
            // whole point of the trip out of sight (1.39.1).
            setRest(true);
          });
          const off = document.createElement('button');
          off.type = 'button';
          off.className = 'ghost';
          off.textContent = 'Take it off';
          off.addEventListener('click', () => {
            void run(ctx => detachRoleEvents(ctx, n.id, r.id),
              `No longer part of ${r.title || 'that'}.`);
          });
          li.append(b, off);
          return li;
        }));
      }
    }

    if (PERSON && PEOPLE && PEOPLE_LIST) {
      const st = session.state();
      // The names already in this vault, so the second thing you link to somebody
      // does not become a second one of them through a typo.
      //
      // A PICK FILLS THE FIELD HERE, and does not link. That is the one place
      // these three lists differ, and the difference is the relation chooser
      // beside the box: a person link carries how they are involved as well as
      // who they are, and a tap cannot know which. Committing on the tap would
      // silently choose "they owe me this" for somebody who never looked.
      //
      // Everybody is offered, including anyone already on this thing — the same
      // person can honestly be owed one thing and running another.
      paintPicks(PEOPLE, peopleNodes(st), [], p => {
        PERSON.value = p.title || '';
        LIVE.textContent = `${p.title || 'That name'} — choose how they are involved, then Link.`;
      });

      // Stakeholders render in their OWN group (1.9.0) — listing them here
      // too would put one link on the sheet twice, and a removal would leave
      // the stale copy contradicting the record (the OPR defect's shape).
      // DOORS, not dead text (1.12.0). A name here was a `<span>`, so the one
      // question a name raises — "what else is with them?" — had no way to be
      // asked from the place it was raised. Same fix 1.6.0 made for the other
      // lists, missed here; the fresh-node lookup is that pattern's own rule,
      // because a row built at paint time can be tapped much later.
      PEOPLE_LIST.replaceChildren(...n.people.filter(l => l.relation !== 'stakeholder').map(l => {
        const li = document.createElement('li');
        li.className = 'detail-feed';
        const who = st.nodes.get(l.person)?.title || '(unnamed)';
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'linklike';
        b.textContent = `${who} — ${RELATION_WORDS[l.relation] ?? l.relation}`;
        b.addEventListener('click', () => {
          const person = session.state().nodes.get(l.person);
          if (!person) return;
          showNode(person);
          // A person's sheet is almost entirely the folded half — "what is with
          // them" lives there. Opening a new item folds it back by design, so
          // this navigation has to say it wants it open, or tapping a name lands
          // you on a sheet whose whole point is out of sight (1.39.1).
          setRest(true);
        });
        li.append(b);
        // AND A PROMISE CAN BE TAKEN BACK (2.20.0). Only this relation gets the
        // control, and the asymmetry is the point rather than an omission: the
        // other four are notes about how somebody is involved, and a wrong one
        // is cosmetic. A promise is a standing claim that you owe somebody
        // something, and one nobody could take back would be the exact ledger
        // `src/requests.ts` says this app exists not to keep.
        //
        // It releases the UNDERTAKING, never the work. The node keeps its clock,
        // its place and its date — somebody who no longer owes Sam a thing may
        // still intend to do it, and deleting their work would be the app
        // deciding that for them.
        if (l.relation === 'promised-to') {
          const off = document.createElement('button');
          off.type = 'button';
          off.className = 'ghost';
          off.textContent = 'No longer promised';
          off.addEventListener('click', () => {
            void run(ctx => releasePromiseEvents(ctx, n.id, l.person),
              'No longer promised. The work is still here.');
          });
          li.append(off);
        }
        // AND SO CAN A DIRECTORY POINTER (3.20.0, ADR-0122), for the same
        // reason turned outward: where the rest of a thing sits is a fact that
        // moves — they wrote it down, it was handed over, it settled — and a
        // pointer nobody could correct would go on asserting it after it
        // stopped being true. The release takes the POINTER, never the work.
        if (l.relation === 'rest-with-them' || l.relation === 'rest-with-me') {
          const rel = l.relation;
          const off = document.createElement('button');
          off.type = 'button';
          off.className = 'ghost';
          off.textContent = rel === 'rest-with-them' ? 'No longer holds the rest' : 'I no longer hold the rest';
          off.addEventListener('click', () => {
            void run(ctx => releaseHoldingEvents(ctx, n.id, l.person, rel),
              'That note is off. The work is still here.');
          });
          li.append(off);
        }
        return li;
      }));

      // WHAT IS WITH THEM (1.12.0) — the per-person half of build-plan item 33.
      // `personView` has been written, exported and unit-tested since the person
      // work landed, with NO caller anywhere: a projection with nowhere to
      // render, the same "complete and unreachable" shape `node.merged` had
      // before 1.7.0. A person is an ordinary node, so its own sheet is the
      // natural home and this costs no new surface on the landing page.
      // Shown only on a person, and shown even when nothing is with them —
      // 'nothing is with them just now' is an answer, and a group that vanishes
      // leaves the question looking unanswerable.
      const personGroup = q<HTMLElement>('#detail-person-group');
      if (personGroup) personGroup.hidden = !(n.kind === 'person' && !n.trashed);
      if (n.kind === 'person') {
        const view = personView(st, n.id, new Date(now()).toISOString(), session.zone);
        const owes = view?.owes ?? [];
        const involves = view?.involves ?? [];
        if (personCount) {
          // A count of what is WITH someone is a fact about work, not a score
          // about them (law 7: the app plots, the human interprets). It never
          // says "late", never ranks people, and never compares two of them.
          personCount.textContent = (view?.total ?? 0) === 0
            ? 'Nothing is with them just now.'
            : owes.length === 0
              ? 'Nothing is owed to you. They come up here:'
              : `${owes.length === 1 ? 'One thing is' : `${owes.length} things are`} owed to you.`;
        }
        const row = (line: { node: NodeState; relation: string; days: number | null }, owed: boolean): HTMLLIElement => {
          const li = document.createElement('li');
          li.className = 'detail-feed';
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'linklike';
          b.textContent = line.node.title || '(untitled)';
          const when = document.createElement('span');
          when.className = 'detail-when';
          // A duration, never a verdict — the same words the People surface
          // uses, so one fact reads identically wherever it appears.
          when.textContent = owed
            ? (waitingWords(line.days) ?? 'with them')
            : (RELATION_WORDS[line.relation] ?? line.relation);
          b.addEventListener('click', () => {
            const fresh = session.state().nodes.get(line.node.id);
            if (fresh) showNode(fresh);
          });
          li.append(b, when);
          return li;
        };
        personOwes?.replaceChildren(...owes.map((l: PersonLine) => row(l, true)));
        personInvolves?.replaceChildren(...involves.map((l: PersonLine) => row(l, false)));
      }

      // WHAT IS ON THIS LINE (3.12.0, ADR-0115) — the person lens's shape, one
      // axis over. A role is an ordinary node, so its own sheet is the home,
      // and the group renders even when the line is empty because that is the
      // answer somebody opened it for.
      const lineGroup = q<HTMLElement>('#detail-line-group');
      if (lineGroup) lineGroup.hidden = !(n.kind === 'role' && !n.trashed);
      if (n.kind === 'role') {
        const view = lineView(st, n.id, heldWork);
        const lineCount = q<HTMLElement>('#detail-line-count');
        if (lineCount) lineCount.textContent = view ? lineViewWords(view) : '';
        const lineRow = (m: NodeState, where: string): HTMLLIElement => {
          const li = document.createElement('li');
          li.className = 'detail-feed';
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'linklike';
          b.textContent = m.title || '(untitled)';
          b.addEventListener('click', () => {
            const fresh = session.state().nodes.get(m.id);
            if (fresh) showNode(fresh);
          });
          const when = document.createElement('span');
          when.className = 'detail-when';
          when.textContent = where;
          li.append(b, when);
          return li;
        };
        // WHERE EACH THING SITS, from the same `servesNode` the runway card
        // uses — so one item's horizon reads identically here and there.
        q<HTMLElement>('#detail-line-work')?.replaceChildren(
          ...(view?.work ?? []).map(m => {
            const h = servesNode(st, m);
            return lineRow(m, h ? `serves ${h.title || '(untitled)'}` : 'under no horizon');
          }));
        // AND THE DISTINCT PARTS OF THE TREE IT RUNS THROUGH. Doors too: moving
        // between a line and the containers it crosses is the walk that was
        // impossible, and a dead-ended list would only prove the point.
        q<HTMLElement>('#detail-line-crosses')?.replaceChildren(
          ...(view?.crosses ?? []).map(h => lineRow(h, 'this line runs through it')));
      }

      // An open waiting-for says how long, in words, and offers the one action
      // that ends it. A duration, never a verdict: "for three weeks" is a fact
      // about a date, and this app keeps score on nobody's behalf.
      if (isOpenWaiting(n)) {
        const li = document.createElement('li');
        li.className = 'detail-feed';
        const label = document.createElement('span');
        const whom = withWhom(st, n);
        const how = waitingWords(openDays(n, new Date(now()).toISOString(), dayOf(session)));
        label.textContent = [whom ? `With ${whom}` : 'With someone', how].filter(Boolean).join(' ') + '.';
        const got = document.createElement('button');
        got.type = 'button';
        got.id = 'detail-waiting-close';
        got.textContent = 'It arrived';
        got.addEventListener('click', () => {
          void run(ctx => closeWaitingEvents(ctx, n.id), 'Good — it is with you now.');
        });
        li.append(label, got);
        PEOPLE_LIST.append(li);
      }
    }

    // Containment (law 4). Where it sits, what may hold it, and what it holds.
    if (PARENT && PLACE && KIDS) {
      const st = session.state();
      paintParents(n);
      paintAfter(n);

      // WHERE IT SITS, AND A WAY TO GET THERE (3.6.0). Every list on this sheet
      // travels DOWN — a child row opens the child's sheet — and nothing
      // traveled up. The place line named the parent in prose, so somebody who
      // had just made a container by filing something under it was told the
      // container's name and handed no route to it. Reported on device in those
      // terms: the thing was made, and there was no way to see it.
      //
      // ONE HOP, on the same mechanism the child rows already use, so the chain
      // is walkable a step at a time in both directions. This is NOT the climb
      // law 4 forbids. That law's subject is ALTITUDE — never having to walk a
      // hierarchy to plan a day — and NOTES records it being misread as a rule
      // about navigation and then cited to close a routing defect. Nothing here
      // asks anybody to climb anything in order to do their work; the runway is
      // still the only workspace and this sheet is still reached from it.
      //
      // A DOOR ONLY WHERE THERE IS SOMEWHERE TO GO. `placeWords` has three
      // other readings — loose, parent not here, parent let go — and each stays
      // prose, because a control that opens nothing is this same defect wearing
      // the other face.
      const place = placeWords(st, n);
      // WHAT IS INSIDE IT, ABOVE THE FOLD (3.23.23). `#detail-children` renders
      // the doors and is inside the region `#detail-more` hides, so a container
      // answered "what is in this" one press deeper than anybody looks — a cold
      // read opened a project holding four things and reported no list of them.
      //
      // `contentsWords` rather than a second walk of the children: it is the
      // projection built for exactly this in 1.27.0, its cap is law 8, and its
      // docblock cites the out-of-sight collision this is an instance of. It
      // returns null for an empty container, and this asks `isContainer` first,
      // so nothing else on the sheet gains a line.
      if (holdingLine) {
        const holding = isContainer(n) ? contentsWords(session.state(), n) : null;
        holdingLine.textContent = holding ?? '';
        holdingLine.hidden = holding === null;
      }
      PLACE.hidden = !place;
      const up = n.parent ? st.nodes.get(n.parent) : undefined;
      if (place && up && !up.trashed && !up.mergedInto) {
        const door = document.createElement('button');
        door.type = 'button';
        door.className = 'linklike detail-place-open';
        door.textContent = place;
        door.addEventListener('click', () => {
          const fresh = session.state().nodes.get(up.id);
          if (fresh) { render(fresh); LIVE.textContent = ''; }
        });
        PLACE.replaceChildren(door);
      } else {
        PLACE.textContent = place ?? '';
      }

      // What it holds, shown on the sheet of the thing that holds it — because
      // "is anything actually under this" is the question Review answers from
      // the outside, and someone looking at the container deserves the same
      // answer without being sent anywhere.
      const kids = childrenOf(st, n.id);
      // Doors (1.6.0): a child row opens the CHILD's sheet — the same sheet,
      // re-rendered on the fresh node, which is how every list travels now.
      KIDS.replaceChildren(...kids.map(k => {
        const li = document.createElement('li');
        li.className = 'detail-feed';
        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'linklike detail-child-open';
        open.textContent = k.title || '(untitled)';
        open.addEventListener('click', () => {
          const fresh = session.state().nodes.get(k.id);
          if (fresh) { render(fresh); LIVE.textContent = ''; }
        });
        li.append(open);
        return li;
      }));
      if (isContainer(n) && kids.length === 0) {
        const li = document.createElement('li');
        li.className = 'detail-feed-words';
        li.textContent = 'Nothing is under this yet.';
        KIDS.append(li);
      }
    }

    // The dependency edge (build-plan item 27). The picker offers only nodes it
    // could legally feed — live, not itself, and not one that would close a
    // loop. Offering an illegal option and refusing it afterwards is a control
    // that lies about what it does.
    if (FEEDS && LEAD && FEEDS_LIST) {
      const st = session.state();
      // One builder with the after-picker (3.20.2, `feedCandidates`): this
      // inline filter had everything except KIND, so people and places were
      // offered as things work could queue behind. Only the already-fed
      // exclusion stays here, because it is about this node's list, not about
      // candidacy.
      const legal = feedCandidates(st, n.id)
        .filter(t => !n.feeds.includes(t.id));
      const keep = FEEDS.value;
      FEEDS.replaceChildren(...[
        Object.assign(document.createElement('option'), { value: '', textContent: 'nothing yet' }),
        ...legal.map(t => Object.assign(document.createElement('option'), {
          value: t.id, textContent: t.title || '(untitled)',
        })),
      ]);
      if (legal.some(t => t.id === keep)) FEEDS.value = keep;

      const view = dependencyView(st, n, new Date(now()).toISOString(), session.zone);
      const words = dependencyWords(view);
      FEEDS_LIST.replaceChildren(...view.feeds.map(f => {
        const li = document.createElement('li');
        li.className = 'detail-feed';
        const label = document.createElement('span');
        label.textContent = f.node.title || '(untitled)';
        const drop = document.createElement('button');
        drop.type = 'button';
        drop.className = 'ghost';
        drop.textContent = 'Unlink';
        drop.setAttribute('aria-label', `Unlink ${f.node.title || '(untitled)'}`);
        drop.addEventListener('click', () => {
          void run(ctx => releaseFeedsEvents(ctx, n.id, f.node.id), 'Unlinked.');
        });
        li.append(label, drop);
        return li;
      }));
      // The arithmetic, in words, only when every term is really there.
      if (words) {
        const p = document.createElement('li');
        p.className = 'detail-feed-words';
        p.textContent = words;
        FEEDS_LIST.append(p);
      }
      if (n.leadDays && n.leadDays > 0) LEAD.value = String(n.leadDays);
    }

    // Only offer what this item can actually do.
    const show = (sel: string, on: boolean): void => {
      const b = btn(sel);
      if (b) b.hidden = !on;
    };
    // Carrying a cadence, whatever kind it is. Gated on `kind === 'upkeep'`
    // this hid "Stop repeating" from every container with a rhythm — a state
    // you could enter and not leave, which is the shape §113 is about.
    const repeats = (n.intervalDays ?? 0) > 0;
    const isPlaceForWork = isContainer(n);
    // No temporal controls on a Menu item OR on a demand-free kind — and the
    // second clause was MISSING until 1.17.2, which was a shipped instance of
    // offered-then-refused (the 1.9.2 audit's F-B, a fourth time). The comment
    // here even named the gap while the code walked into it: it said "the
    // gate's law-6 check guards demand-free KINDS, not Menu membership" and then
    // tested only Menu membership. So the sheet offered "Not before ⟨day⟩" on a
    // person or an anchor reached through search — and an off-Menu aspiration in
    // the todo list — and the gate refused all of it after the tap. The repeat
    // verb too: `makeRepeatEvents` carries a `clock.set`, refused the same way.
    //
    // Both clauses still matter. Menu membership is not a kind — a
    // someday-routed action keeps kind 'action', so without the first clause a
    // date on a wish would be ACCEPTED and then unrenderable (the Menu group
    // wins every surface). Dating a wish goes through "Bring back as real work"
    // first, which is the deliberate act law 6 wants it to be.
    const temporal = !n.onMenu
      && !(DEMAND_FREE_KINDS as readonly string[]).includes(n.kind);
    const grp = (sel: string, on: boolean): void => {
      const g = q<HTMLElement>(sel);
      if (g) g.hidden = !on;
    };
    grp('#detail-date-group', temporal);
    grp('#detail-start-group', temporal);
    grp('#detail-repeat-group', temporal);
    // The same two controls, and a container is not being told it will become a
    // chore. What a goal does on a rhythm is come back to be looked at.
    const repeatLabel = q('#detail-repeat-label');
    if (repeatLabel) repeatLabel.textContent = isPlaceForWork ? 'Come back to this' : 'Make it repeat';
    const repeatSet = btn('#detail-repeat-set');
    if (repeatSet) repeatSet.textContent = isPlaceForWork ? 'Come back' : 'Repeat';
    const repeatStop = btn('#detail-repeat-stop');
    if (repeatStop) repeatStop.textContent = isPlaceForWork ? 'Stop coming back' : 'Stop repeating';
    // Only meaningful once something repeats: an arrangement IS an upkeep, and
    // saying "it runs itself" about a thing with no rhythm would be a marker
    // with nothing behind it.
    grp('#detail-arrangement-group', temporal && n.kind === 'upkeep');
    // The estimate is about DOING the thing: meaningless on a wish, a person,
    // or something let go — and junk rows would pollute the one dataset that
    // can never be backfilled (audit).
    grp('#detail-estimate-group', !n.onMenu && !n.trashed
      && !['person', 'aspiration', 'pebble'].includes(n.kind));
    // WHAT ACTUALLY HAPPENED, when it has been timed (V2 stage 5). The two ends
    // and never an average — task durations are tau-heavy and the mean sits in
    // the gap where almost nothing lands. Hidden entirely when there is nothing
    // to say, rather than showing a zero or an empty range.
    const took = q<HTMLElement>('#detail-took');
    if (took) {
      const words = rangeWords(timedRange(n));
      took.textContent = words ?? '';
      took.hidden = words === null;
    }
    // A note is NOT a demand, so unlike the temporal groups it stays for Menu
    // items and people — anything you hold can carry words. Only a thing let
    // go loses the editor; "Keep it after all" is the door back.
    grp('#detail-note-group', !n.trashed);
    // Shown wherever a note is. A situation is about DOING the thing, so it is
    // meaningless on a person or an anchor for the same reason the estimate is —
    // but `!n.trashed` is the note's own rule and a want on the Menu can still
    // carry "next time I'm in town", which is exactly the cue this is for.
    // The weight is about DOING a thing, so it is offered wherever work is —
    // the same rule the situation follows, and for the same reason.
    grp('#detail-weight-group', !n.trashed && !n.released
      && !(DEMAND_FREE_KINDS as readonly string[]).includes(n.kind));
    {
      const w = weightOf(n);
      const now = q<HTMLElement>('#detail-weight-now');
      if (now) {
        now.textContent = w ? `You called this ${w}.` : '';
        now.hidden = !w;
      }
      const clear = btn('#detail-weight-clear');
      if (clear) clear.hidden = !w;
      // The one you already chose reads as chosen, so the row is a state and not
      // three identical offers.
      for (const k of ['light', 'ordinary', 'heavy']) {
        btn(`#detail-weight-${k}`)?.setAttribute('aria-pressed', String(w === k));
      }
    }
    grp('#detail-situation-group', !n.trashed);
    // WHAT HAS TO HAPPEN FIRST. Narrower than the note's rule on purpose: a
    // demand-free kind is covered by being on a surface and is never finished,
    // so an anchor either way would be inert — the gate refuses one pointing AT
    // such a kind, and one hung ON such a kind buys nothing. A resume card is a
    // way back into a thread and spends itself when picked up; queueing behind
    // it, or queueing it behind something, is a promise neither can keep.
    grp('#detail-after-group', !n.trashed
      && !(DEMAND_FREE_KINDS as readonly string[]).includes(n.kind)
      && n.kind !== 'resume-card');
    // The meeting furniture (1.9.0, ADR-0057): a container's people and its
    // decisions. Shown for containers off the Menu — and for anything that
    // ALREADY carries one, so a stakeholder or a decision can never become
    // invisible because the thing it is attached to changed kind.
    {
      const stakes = stakeholdersOf(session.state(), n);
      const container = isContainer(n) && !n.trashed && !n.onMenu;
      grp('#detail-stakeholder-group', container || stakes.length > 0);
      // Visibility asks the SAME reader as the content (1.9.2). `n.decisions`
      // alone would hide the group on a survivor whose only decisions came
      // from something folded into it — the list full and the group closed.
      grp('#detail-decision-group', container || decisionsFor(session.state(), n).length > 0);
      // The editor goes when a thing is let go; the record stays readable.
      show('#detail-decision-set', !n.trashed);
      if (decisionInput) decisionInput.hidden = n.trashed;
      paintMeeting(n);
    }
    // Someone asked (1.8.0, ADR-0056). Hidden on Menu items — park is a
    // demand clock and the gate's belt refuses it on a wish, so the verb is
    // never offered where it cannot land — and on people and resume cards.
    {
      // Every demand-free kind, not only `person` (1.17.3, the seam audit):
      // declining writes a park in the same batch, and the gate refuses a park
      // on a demand-free kind — so "Not mine to carry" on an anchor reached
      // through search, or an off-Menu aspiration in the todo list, was offered
      // and then refused. The same shape 1.17.2 closed for dates, one group down.
      const canDecline = !n.trashed && !n.mergedInto && n.onMenu === null
        && !(DEMAND_FREE_KINDS as readonly string[]).includes(n.kind)
        && n.kind !== 'resume-card';
      grp('#detail-request-group', canDecline);
      if (canDecline) paintRequest(n);
    }
    // The fold verb (1.7.0): only for a thing that is its own thing. A merged
    // node shows the way BACK instead, and the survivor lists what it holds.
    grp('#detail-merge-group', !n.trashed && !n.mergedInto);
    grp('#detail-unmerge-group', Boolean(n.mergedInto));
    if (!n.trashed && !n.mergedInto) paintMergeTargets(n);
    {
      // TRANSITIVE (1.9.2): in a chain A -> B -> C, one hop left A's "split it
      // back out" reachable from no surface at all. ADR-0053 says the way back
      // must outlive the sitting — for every node in the chain, not just the last.
      const folded = foldedIntoDeep(session.state(), n.id);
      grp('#detail-merged-group', folded.length > 0);
      if (mergedList) {
        mergedList.replaceChildren(...folded.map(f => {
          const li = document.createElement('li');
          li.className = 'detail-feed';
          const label = document.createElement('span');
          label.textContent = f.title || '(untitled)';
          const split = document.createElement('button');
          split.type = 'button';
          split.className = 'ghost';
          // Leads with the visible words so saying what is written on it works
          // (SC 2.5.3); the title still disambiguates one row from the next (§4).
          split.textContent = 'Split it back out';
          split.setAttribute('aria-label', `Split it back out — ${f.title || '(untitled)'}`);
          split.addEventListener('click', () => {
            void run(ctx => unmergeEvents(ctx, f.id), 'Split back out — its own thing again.');
          });
          li.append(label, split);
          return li;
        }));
      }
    }
    // Composed Today's verb (1.6.0): only when the module is on, only for
    // choosable things. At the cap the button says so and disables — a
    // control that would fail after the tap is a control that lies.
    {
      const stNow = session.state();
      const on = todayIsOn(stNow) && choosable(n);
      grp('#detail-today-group', on);
      if (on) {
        const iso = new Date(now()).toISOString();
        const chosenNow = chosenToday(stNow, n.id, iso, session.zone);
        show('#detail-today-add', !chosenNow);
        show('#detail-today-remove', chosenNow);
        const add = btn('#detail-today-add');
        if (add && !chosenNow) {
          const full = composedFull(stNow, iso, session.zone);
          add.disabled = full;
          add.textContent = full ? 'Today is full — a hand fits five' : 'Put it in today';
        } else if (add) {
          add.disabled = false;
          add.textContent = 'Put it in today';
        }
      }
    }
    show('#detail-date-clear', Boolean(n.clocks.due));
    show('#detail-start-clear', Boolean(n.clocks.start));
    show('#detail-repeat-stop', repeats);
    show('#detail-done', !n.lastDone && !n.trashed);
    show('#detail-undone', Boolean(n.lastDone));
    show('#detail-menu', !n.onMenu && !n.trashed);
    // The picker stands with the button — AND STAYS once the thing is on the
    // Menu, so a category chosen wrongly can be corrected in place. Without
    // that it would be a state you can enter and not leave (LESSONS 113): the
    // only way back would be to bring the thing off the Menu and put it on
    // again, which writes two events to fix one word.
    {
      const cat = q<HTMLSelectElement>('#detail-menu-category');
      if (cat) {
        cat.hidden = n.trashed;
        // Shows what it IS while it is on the Menu, and what it WOULD BE before
        // that. Either way the control never disagrees with the surface.
        cat.value = n.onMenu ?? 'read';
      }
    }
    show('#detail-promote', Boolean(n.onMenu));
    // Not on a merge SURVIVOR (1.17.3, the seam audit): trashing a node that
    // others folded into makes the folded-in nodes newly silent and the gate
    // refuses the batch after the tap — the error toast even leaked gate
    // internals ("batch would leave 1 silent node(s)"). The way to let a
    // survivor go is on this same sheet: split the folds back out first, and
    // the merged-group below already offers exactly that.
    show('#detail-trash', !n.trashed && !n.released && foldedIntoDeep(session.state(), n.id).length === 0);
    show('#detail-untrash', n.trashed);
    // Offered only where it means something. Not on a trashed node — that has
    // already ended, and two ends is a question with no answer — and not on a
    // demand-free kind, which is never carried in the first place.
    show('#detail-release', !n.trashed && !n.released
      && !(DEMAND_FREE_KINDS as readonly string[]).includes(n.kind));
    show('#detail-reclaim', !!n.released);
    // The hint explains the verb, so it belongs with the verb and goes when the
    // verb goes. A standing paragraph about a control that is not on screen is
    // noise on a sheet that is already long.
    show('#detail-release-hint', !n.trashed && !n.released
      && !(DEMAND_FREE_KINDS as readonly string[]).includes(n.kind));
    // "On its own" only when there is something to come out of, and the promote
    // to a container only when it is not one already — the same rule as every
    // other control here: never offer what this item cannot do.
    // Save-for numbers, only for a Menu item in that category — a target on
    // something you are not saving for is a field with nothing to mean.
    const saveGroup = q('#detail-savefor-group');
    if (saveGroup) {
      const isSaveFor = n.onMenu === 'save-for';
      saveGroup.hidden = !isSaveFor;
      if (isSaveFor) {
        const t = q<HTMLInputElement>('#detail-save-target');
        const v = q<HTMLInputElement>('#detail-save-saved');
        // Do NOT clobber what someone is part-way through typing — the same
        // rule the rename box already carries, for the same reason.
        if (t && document.activeElement !== t) t.value = n.saveTarget != null ? String(n.saveTarget) : '';
        if (v && document.activeElement !== v) v.value = n.saveSaved != null ? String(n.saveSaved) : '';
      }
    }

    show('#detail-unparent', Boolean(n.parent));
    show('#detail-make-project', !isContainer(n) && !n.trashed);
    // THE MIRROR OF THE LINE ABOVE, and deliberately its exact complement: the
    // promotion is offered while this is not a container, the correction once
    // it is. A thing cannot be offered both at once, and nothing can be offered
    // neither — which is the property the walk asserts rather than this comment.
    show('#detail-kind-row', isContainer(n) && !n.trashed);
    // SET FROM THE NODE, NOT LEFT WHERE THE READER LAST PUT IT. This sheet is
    // reused for every node, so a select left holding the previous container's
    // kind states something false about this one before anybody touches it —
    // the same residue class the cleared PLACE and APPROACH lines above exist
    // for. Skipped while it has focus, so a repaint cannot move a choice out
    // from under somebody mid-decision.
    if (kindSel && isContainer(n) && document.activeElement !== kindSel) {
      kindSel.value = n.kind;
    }
    // History stays live while its disclosure is open — a commit from this
    // sheet should show its own line the moment it lands.
    if (historyEl?.open) buildHistory(n.id);
    // THE TRACK ROLE BELONGS TO CONTAINERS. A role on a single action would be
    // a label with nothing under it to govern. `!n.onMenu`, because the gate's
    // Menu belt would refuse the write and offering a choice the gate refuses
    // is the recorded anti-pattern (audit; ADR-0038).
    const container = isContainer(n) && !n.trashed && !n.onMenu;
    const trackRow = q('#detail-track-row');
    if (trackRow) trackRow.hidden = !container;
    // THE ANSWER-OWED DATE DOES NOT (3.24.9), and it was in the same boolean as
    // the track role for its whole life.
    //
    // A suspense is "the date you owe somebody an answer" — and until this
    // release it could be recorded only on a project, area, goal or outcome.
    // "I'll get back to you Thursday" about ONE THING, which is the commonest
    // thing a conversation produces, had nowhere to live: not on an action, not
    // on a waiting-for, not on an upkeep.
    //
    // THE GATE NEVER REQUIRED THIS. Law 6 refuses a demand clock on a
    // demand-free KIND and the Menu belt refuses one on a Menu PLACEMENT, which
    // is exactly what `temporal` above already computes for the date group four
    // hundred lines up. The container restriction came from the comment it
    // shared with the track role — an argument about labels, made once, applied
    // to both, and never measured against the thing it bore on: the v1
    // definition of done, whose second clause is "every suspense lives in the
    // app". Every suspense could not.
    //
    // `setSuspenseEvents` is unchanged. The write, the fold, the gate's
    // placement belt and every reader — the carrying report, the replan card,
    // the calendar export, the dependency arithmetic — already worked on any
    // node; nothing was ever restricted but the row.
    const suspRow = q('#detail-suspense-row');
    if (suspRow) suspRow.hidden = !(temporal && !n.trashed);

    // HOT OR COLD, REVISABLE (Phase 0, read 7). Shown on anything that is still
    // yours: heat is a feeling about a thing rather than a demand on it — no
    // clock, no accrual, nothing turning a color as time passes — so law 6 is
    // not in play and a wish on the Menu is included deliberately, because
    // `offerNow` rides one Menu item into the offer beside real work and the
    // tie-break reads heat there too. Hidden on a thing let go or folded away,
    // which is the same test every other act on this sheet uses.
    const heatRow = q('#detail-heat-row');
    if (heatRow) heatRow.hidden = Boolean(n.trashed || n.mergedInto);
    show('#detail-track', container && n.role !== 'track');
    show('#detail-untrack', container && n.role === 'track');
    const susp = q<HTMLInputElement>('#detail-suspense');
    if (susp && n.clocks.suspense) susp.value = localDayKey(n.clocks.suspense.at, dayOf(session));
  }

  /** A positive whole number, or null. A blank or nonsense box must not become
   *  NaN in the log — a NaN cadence made an item shout the loudest phrase in the
   *  app and, worse, could make it un-completable (audit). */
  const positiveInt = (el: HTMLInputElement): number | null => {
    const v = Number(el.value);
    return Number.isFinite(v) && Number.isInteger(v) && v > 0 ? v : null;
  };

  const doRename = (): void => {
    const next = NAME.value.trim();
    if (!next) { say('It needs to say something.'); return; }
    if (next === current?.title) { say('That is what it already says.'); return; }
    void run(ctx => renameEvents(ctx, current!.id, next), `Now reads "${next}".`);
  };
  btn('#detail-rename')?.addEventListener('click', doRename);
  NAME.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); doRename(); }
  });


  // THE SAME QUESTION THE FILL ASKS. Reading `due` here while the box is filled
  // from `review` is what left the unsaved line permanently on.
  const storedDue = (): string => {
    const own = ownDateClock(current);
    return own ? localDayKey(own.at, dayOf(session)) : '';
  };
  const storedStart = (): string =>
    current?.clocks.start ? localDayKey(current.clocks.start.at, dayOf(session)) : '';
  const storedSuspense = (): string =>
    current?.clocks.suspense ? localDayKey(current.clocks.suspense.at, dayOf(session)) : '';

  const paintUnsaved = [
    unsaved('#detail-date', '#detail-date-unsaved', storedDue),
    unsaved('#detail-start', '#detail-start-unsaved', storedStart),
    unsaved('#detail-suspense', '#detail-suspense-unsaved', storedSuspense),
  ];
  const refreshUnsaved = (): void => { for (const p of paintUnsaved) p(); };

  // "More about this" (1.39.1). Twenty-four groups sat open on every item; four
  // of them are what most items need. The rest fold, and folding is not hiding —
  // one press, nothing moved, nothing removed.
  //
  // NOT remembered between items on purpose. A sheet that opens differently
  // depending on what you did to the last thing is a sheet you cannot learn, and
  // this app's whole bargain is that the shape never changes.
  const moreBtn = btn('#detail-more');
  const rest = q('#detail-rest');
  const setRest = (open: boolean): void => {
    if (!rest || !moreBtn) return;
    rest.hidden = !open;
    moreBtn.setAttribute('aria-expanded', String(open));
    moreBtn.textContent = open ? 'Less about this' : 'More about this';
  };
  moreBtn?.addEventListener('click', () => {
    setRest(moreBtn.getAttribute('aria-expanded') !== 'true');
  });


  /**
   * A DAY YOU JUST PICKED, SPOKEN THE WAY THE APP SAYS DAYS.
   *
   * These three confirmations read the date input's own key straight out —
   * "Due 2026-09-09." — into the live region, so the one moment the app speaks
   * a date aloud was the one place it spoke storage format. The words existed
   * (`clockDayWords`) and took an instant, and the key had no route to one.
   *
   * It does now, and it is the SAME route the emitter takes: `endOfDayKey` is
   * exactly what `setDueEvents`, `setStartEvents` and `setSuspenseEvents` run
   * the key through before storing it, so what is said and what is kept cannot
   * describe one day two ways.
   */
  const dayPicked = (key: string): string => clockDayWords(
    endOfDayKey(key, session.zone),
    new Date(now()).toISOString(), session.zone, dayOf(session));

  btn('#detail-date-set')?.addEventListener('click', () => {
    const key = DATE.value;
    // A date input yields '' when empty or invalid; nothing is a legal answer.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) { say('Pick a date first.'); return; }
    void run(ctx => setDueEvents(ctx, current!.id, key, isContainer(current!)),
      isContainer(current!) ? `Comes back ${dayPicked(key)}.` : `Due ${dayPicked(key)}.`);
  });
  btn('#detail-date-clear')?.addEventListener('click', () => {
    void run(ctx => clearDueEvents(ctx, current!.id, isContainer(current!)),
      'Date removed — it comes back to you today.');
  });
  btn('#detail-start-set')?.addEventListener('click', () => {
    const key = startInput?.value ?? '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) { say('Pick a day first.'); return; }
    void run(ctx => setStartEvents(ctx, current!.id, key),
      `Out of the way until ${dayPicked(key)} — it comes back on its own.`);
  });
  btn('#detail-start-clear')?.addEventListener('click', () => {
    void run(ctx => clearStartEvents(ctx, current!.id),
      'Cleared — it is back with you today.');
  });
  btn('#detail-estimate-set')?.addEventListener('click', () => {
    if (!estimateInput) return;
    const v = Number(estimateInput.value);
    if (!Number.isInteger(v) || v < 1) { say('Whole minutes, at least 1.'); return; }
    void run(ctx => estimateEvents(ctx, current!.id, v), 'Noted — nothing checks up on it.');
  });
  btn('#detail-merge-set')?.addEventListener('click', () => {
    if (!mergeSel || !current) return;
    const targetId = mergeSel.value;
    if (!targetId) { say('Pick the one that stays first.'); return; }
    // Fresh on BOTH sides — the sheet can sit open while the world moves.
    const st = session.state();
    const source = st.nodes.get(current.id);
    const target = st.nodes.get(targetId);
    if (!source || source.trashed || source.mergedInto
      || !target || target.trashed || target.mergedInto) {
      say('One of them changed while this was open — pick again.');
      paintMergeTargets(current);
      return;
    }
    // The SECOND lock (1.9.2). Legality was computed when the list was built,
    // but the sheet can sit open while the world moves — the target may have
    // gone onto the Menu since. Ask the gate's own predicate again rather than
    // hand it a batch it must refuse.
    if (!canHold(target, source)) {
      say('That one is on the list of wishes now — a wish holds no demands.');
      paintMergeTargets(current);
      return;
    }
    const title = target.title || '(untitled)';
    // Built once so the words can name what could not come across. A skip
    // nobody is told about is the silent swallow this release exists to end.
    let skippedFeeds = 0;
    let skippedAfter = 0;
    void run(ctx => {
      const plan = mergePlan(ctx, session.state(), source, target);
      skippedFeeds = plan.skipped.feeds.length;
      skippedAfter = plan.skipped.after.length;
      return plan.events;
    }, () => skippedFeeds + skippedAfter === 0
      ? `Folded into “${title}”. Splitting it back out is right below.`
      // Named separately, because they are different losses: one is something it
      // fed, the other is an order of doing things. A single sentence covering
      // both would tell you a thing was dropped without telling you which.
      : skippedFeeds > 0 && skippedAfter === 0
        ? `Folded into “${title}”. One thing it fed could not come across — `
          + 'that would have made two things each wait for the other.'
        : skippedAfter > 0 && skippedFeeds === 0
          ? `Folded into “${title}”. One thing waiting on it could not be moved across — `
            + 'that would have made two things each wait for the other.'
          : `Folded into “${title}”. Something it fed, and something waiting on it, `
            + 'could not come across — either would have made two things each wait for the other.');
  });
  btn('#detail-unmerge')?.addEventListener('click', () => {
    void run(ctx => unmergeEvents(ctx, current!.id),
      'Split back out — its own thing again, with a clock of its own.');
  });
  // Someone asked (1.8.0). Fresh on commit — the node the batch is built from
  // is live state's, never the stale copy the sheet opened with.
  btn('#detail-decline')?.addEventListener('click', () => {
    void run(ctx => {
      const st = session.state();
      const fresh = st.nodes.get(current!.id);
      return fresh ? declineEvents(ctx, st, fresh) : [];
    }, 'Declined — kept in the Not Now ledger. Nothing will chase you.');
  });
  btn('#detail-carry')?.addEventListener('click', () => {
    void run(ctx => carryEvents(ctx, current!.id),
      'Carried after all — its own thing again, back with you today.');
  });
  // What was decided (1.9.0). Empty writes nothing — unlike a note, an empty
  // decision is not the honest removal of anything.
  btn('#detail-decision-set')?.addEventListener('click', () => {
    if (!decisionInput || !current) return;
    if (!cleanNote(decisionInput.value)) { say('It needs to say something.'); return; }
    const text = decisionInput.value;
    void run(ctx => logDecisionEvents(ctx, current!.id, text), 'Logged. It stays as written.')
      .then(() => { decisionInput.value = ''; });
  });
  btn('#detail-slot-park')?.addEventListener('click', () => {
    void run(ctx => parkToSlotEvents(ctx, session.state(), current!.id),
      'Parked until the request slot.');
  });
  mergeFilter?.addEventListener('input', () => {
    if (current && !current.trashed && !current.mergedInto) paintMergeTargets(current);
  });

  btn('#detail-today-add')?.addEventListener('click', () => {
    void run(ctx => chooseTodayEvents(ctx, current!.id), 'Chosen for today.');
  });
  btn('#detail-today-remove')?.addEventListener('click', () => {
    void run(ctx => releaseTodayEvents(ctx, current!.id), 'Out of today — nothing is counted.');
  });
  btn('#detail-note-set')?.addEventListener('click', () => {
    if (!noteInput || !current) return;
    const clean = cleanNote(noteInput.value);
    // No event for no change — the log must not carry claims about changes
    // that did not happen (the same rule makeRepeatEvents follows).
    const had = noteOf(session.state().nodes.get(current.id) ?? current) ?? '';
    if (clean === had) { say(clean ? 'Already kept.' : 'Nothing to keep yet.'); return; }
    void run(ctx => noteEvents(ctx, current!.id, noteInput.value),
      clean ? 'Kept with it.' : 'Note removed.');
  });
  // WHERE IT STANDS, AND WHAT WOULD CHANGE IT (3.18.0, ADR-0121).
  //
  // The note's rule about a no-op, kept deliberately: no event for no change,
  // because the log must not carry claims about changes that did not happen.
  // Clearing one back to empty IS a change and does write, as an empty string —
  // `node.field.set` is the only field event there is, and taking a thing back
  // is a decision the log should keep rather than an erasure.
  const writeField = (
    input: HTMLTextAreaElement | null,
    read: (n: NodeState) => string | null,
    make: (ctx: StampContext, node: string, text: string) => AppEvent[],
    kept: string, cleared: string,
  ): void => {
    if (!input || !current) return;
    const clean = cleanNote(input.value);
    const had = read(session.state().nodes.get(current.id) ?? current) ?? '';
    if (clean === had) { say(clean ? 'Already kept.' : 'Nothing to keep yet.'); return; }
    void run(ctx => make(ctx, current!.id, input.value), clean ? kept : cleared);
  };
  btn('#detail-stands-set')?.addEventListener('click', () => {
    writeField(standsInput, standsAt, setStandsEvents,
      'Kept. Nothing watches for it.', 'Cleared.');
  });
  btn('#detail-changes-set')?.addEventListener('click', () => {
    // THE CONFIRMATION SAYS WHAT THE APP WILL NOT DO. Somebody writing a
    // condition down is entitled to wonder whether the app is now watching for
    // it, and it is not — it has no way to see their world. Saying so at the
    // moment of writing is cheaper than a reader discovering it by waiting.
    writeField(changesInput, changedBy, setChangesEvents,
      'Written down. The app will not tell you when it happens — you will.', 'Cleared.');
  });
  btn('#detail-situation-set')?.addEventListener('click', () => {
    if (!situationInput || !current) return;
    const clean = cleanNote(situationInput.value);
    // No event for no change, same rule as the note above.
    const had = situationOf(session.state().nodes.get(current.id) ?? current) ?? '';
    if (clean === had) { say(clean ? 'Already kept.' : 'Nothing to keep yet.'); return; }
    void run(ctx => situationEvents(ctx, current!.id, situationInput.value),
      // Says what happened and nothing about whether it was a good plan.
      clean ? 'Kept — it comes back with it.' : 'Removed.');
  });
  btn('#detail-repeat-set')?.addEventListener('click', () => {
    const i = positiveInt(EVERY), c = positiveInt(SLACK);
    if (i === null || c === null) { say('Both numbers need to be whole days, at least 1.'); return; }
    void run(ctx => makeRepeatEvents(ctx, current!.id, current!.kind, i, c), `Repeats ${everyDaysWords(i)}.`);
  });
  btn('#detail-repeat-stop')?.addEventListener('click', () => {
    // The node's OWN kind, so a goal that stops coming back is still a goal.
    // Without it `stopRepeatEvents` writes `from: 'upkeep', to: 'action'` about
    // a node that was never an upkeep — a false claim in an append-only log,
    // and a goal quietly demoted to a task.
    const wasContainer = current ? isContainer(current) : false;
    void run(ctx => stopRepeatEvents(ctx, current!.id, 'action', current!.kind),
      wasContainer ? 'It no longer comes back on its own.' : 'It no longer repeats.');
  });
  btn('#detail-arrangement-set')?.addEventListener('click', () => {
    void run(ctx => markArrangementEvents(ctx, current!.id),
      'Kept as something that runs itself.');
  });
  btn('#detail-arrangement-stop')?.addEventListener('click', () => {
    void run(ctx => unmarkArrangementEvents(ctx, current!.id),
      'Back to something you do yourself.');
  });
  btn('#detail-arrangement-depends')?.addEventListener('click', () => {
    const on = current ? dependsOnOthers(current) : false;
    void run(ctx => (on ? clearDependsEvents : setDependsEvents)(ctx, current!.id),
      on ? 'You can check this one yourself.' : 'Confirming it means asking whoever runs it.');
  });
  btn('#detail-done')?.addEventListener('click', () => {
    void run(ctx => doneEvents(ctx, current!.id), 'Done.');
  });
  btn('#detail-undone')?.addEventListener('click', () => {
    void run(ctx => undoneEvents(ctx, current!.id), 'Back on the list.');
  });
  // THE SAME EVENT THE SORTING PASS WRITES, and the same two sentences it
  // reads back. `heatEvents` is untouched: the pass was never the narrow part,
  // the absence of a second door was.
  btn('#detail-hot')?.addEventListener('click', () => {
    void run(ctx => heatEvents(ctx, current!.id, 'hot'), 'Marked hot.');
  });
  btn('#detail-cold')?.addEventListener('click', () => {
    void run(ctx => heatEvents(ctx, current!.id, 'cold'), 'Marked cold.');
  });
  btn('#detail-menu')?.addEventListener('click', () => {
    // WHICH KIND OF WANT (2.23.0). The picker beside the button, `read` by
    // default — so nothing is required and the common case is one tap, which is
    // the shape `docs/nd-collisions.md` entry 26 permits and the only thing it
    // permits. Before this, both routes a person uses wrote `read`, so the
    // Menu's six-way grouping rendered one group on every store.
    const cat = q<HTMLSelectElement>('#detail-menu-category');
    const chosen = (cat?.value || 'read') as MenuCategory;
    void run(ctx => toMenuEvents(ctx, current!.id, chosen), 'On the Menu — no clock, no demand.');
  });
  // CHANGING IT IN PLACE. `menu.item.added` is last-write-wins on the `menu`
  // stamp, so re-emitting it with a different category is the correction —
  // no new noun, no new control, and nothing to undo. Only while the thing is
  // ALREADY on the Menu: before that the picker is an argument to the button
  // beside it, and firing on change would put things on the Menu by accident.
  q<HTMLSelectElement>('#detail-menu-category')?.addEventListener('change', (e) => {
    if (!current?.onMenu) return;
    const next = (e.target as HTMLSelectElement).value as MenuCategory;
    if (next === current.onMenu) return;
    void run(ctx => toMenuEvents(ctx, current!.id, next), 'Changed on the Menu.');
  });

  // A FIRST STEP, FROM ANYWHERE (2.23.0). The offer card's flow has had one
  // route since 1.24.0 — you could shape the one thing you were handed and
  // nothing else. Same intent, same event, second door.
  btn('#detail-step-set')?.addEventListener('click', () => {
    const input = q<HTMLInputElement>('#detail-step');
    if (!input || !current) return;
    const text = input.value.trim();
    if (!text) { say('A first step first — a few words is enough.'); return; }
    input.value = '';
    void run(ctx => biteEvents(ctx, ctx.id(), current!.id, text), 'Put under this one.');
  });

  btn('#detail-promote')?.addEventListener('click', () => {
    // The node's OWN kind decides what it comes back as — see `promotedKind`.
    // This used to force 'action', so a goal that had been rested on the Menu
    // came back a task, and an upkeep came back a task still carrying its
    // rhythm.
    void run(ctx => promoteNodeFromMenuEvents(ctx, current!), 'Brought back as real work.');
  });
  btn('#detail-trash')?.addEventListener('click', () => {
    void run(ctx => [{
      id: ctx.id(), vault: ctx.vault, at: ctx.at, device: ctx.device, seq: ctx.seq(),
      kind: 'node.trashed', node: current!.id, payload: { reason: 'detail' },
    } as never], 'Let go. You can still keep it after all.');
  });
  btn('#detail-untrash')?.addEventListener('click', () => {
    void run(ctx => untrashEvents(ctx, current!.id), 'Kept.');
  });
  btn('#detail-release')?.addEventListener('click', () => {
    // Says what happened and where the way back is. NOT "well done" and not
    // "that's one less thing" — an approving opinion is still an opinion about
    // the person, and this app does not have one.
    void run(ctx => releaseEvents(ctx, current!.id),
      'Put down. It will not come back on its own; search finds it by name.');
  });
  for (const k of ['light', 'ordinary', 'heavy'] as const) {
    btn(`#detail-weight-${k}`)?.addEventListener('click', () => {
      if (!current) return;
      // Says what was recorded and what it changes. NOT "that's a big one" —
      // sympathy about the work is still an opinion, and the app does not have
      // one about you or about what you are carrying.
      void run(ctx => weightEvents(ctx, current!.id, k),
        k === 'heavy' ? 'Noted — it waits for a better day rather than a longer list.'
          : k === 'light' ? 'Noted — it comes forward on a low stretch.'
            : 'Noted.');
    });
  }
  btn('#detail-weight-clear')?.addEventListener('click', () => {
    if (!current) return;
    void run(ctx => weightEvents(ctx, current!.id, ''), 'Cleared — nobody has said.');
  });
  btn('#detail-reclaim')?.addEventListener('click', () => {
    void run(ctx => reclaimEvents(ctx, current!.id),
      'Back with you — it has a date of its own again.');
  });
  btn('#detail-feeds-set')?.addEventListener('click', () => {
    if (!FEEDS || !LEAD || !current) return;
    const target = FEEDS.value;
    if (!target) { say('Pick what this holds up first.'); return; }
    const lead = positiveInt(LEAD);
    if (lead === null) { say('How many days does this take? A whole number, at least 1.'); return; }
    const title = session.state().nodes.get(target)?.title || 'it';
    void run(ctx => declareFeedsEvents(ctx, current!.id, target, lead), `Linked to ${title}.`);
  });

  btn('#detail-parent-set')?.addEventListener('click', () => {
    if (!PARENT || !current) return;
    const target = PARENT.value;
    if (!target) { say('Pick what it is part of first.'); return; }
    const title = session.state().nodes.get(target)?.title || 'it';
    const prior = current.parent;
    void run(ctx => parentEvents(ctx, current!.id, target, prior), `Now part of ${title}.`);
  });
  btn('#detail-unparent')?.addEventListener('click', () => {
    if (!current) return;
    const prior = current.parent;
    void run(ctx => unparentEvents(ctx, current!.id, prior),
      'On its own again — it still comes back to you.');
  });
  // Narrowing repaints the options live; creating makes the typed place real
  // and files this under it, one gated commit — the gate cures the fresh
  // container with a same-day clock exactly as it cures any creation.
  parentFilter?.addEventListener('input', () => { if (current) paintParents(current); });
  // AND THE PICKER REPAINTS THE BUTTON TOO. Without this the button kept saying
  // "New project" after Goal was chosen — measured, not imagined — which is a
  // control claiming something other than what pressing it does. The write was
  // correct; the sentence above it was not, and the sentence is the part
  // somebody reads before deciding.
  parentKind?.addEventListener('change', () => { if (current) paintParents(current); });
  afterFilter?.addEventListener('input', () => { if (current) paintAfter(current); });
  btn('#detail-after-set')?.addEventListener('click', () => {
    if (!AFTER || !current) return;
    const target = AFTER.value;
    if (!target) { say('Pick the thing that has to happen first.'); return; }
    const t = session.state().nodes.get(target);
    void run(ctx => afterEvents(ctx, current!.id, target),
      // Says what will happen, not that it was a good idea. The second half is
      // the part worth saying: it is the promise clause (e) is making.
      `Waiting for “${t?.title || '(untitled)'}” — it comes back the moment that is done.`);
  });
  btn('#detail-after-clear')?.addEventListener('click', () => {
    if (!current) return;
    void run(ctx => clearAfterEvents(ctx, current!.id),
      'No longer waiting — it is back with you now.');
  });
  // FILLED FROM `CONTAINER_ORDER`, never from a list written out here — a second
  // copy of the kinds is a second thing to keep in step, and this repo has paid
  // for that shape more than once.
  //
  // BOTH SELECTS, ONE LOOP (3.23.31). The correction row added beside the
  // promotion needs the identical option list, and filling it separately would
  // be the second copy this comment already refuses — one loop over one source,
  // so a kind added to `CONTAINER_ORDER` appears in both without anybody
  // remembering the second.
  for (const sel of [parentKind, kindSel]) {
    if (!sel || sel.options.length > 0) continue;
    for (const [kind, words] of CONTAINER_ORDER) {
      const o = document.createElement('option');
      o.value = kind;
      o.textContent = words;
      sel.append(o);
    }
  }
  // Only the CREATION select defaults to `project`. The correction one is set
  // from the node it is describing, in `paint` above — defaulting it here would
  // make every container read as a Project for the instant before the sheet
  // painted, which is the claim this control exists to stop the app making.
  if (parentKind) parentKind.value = CONTAINER_DEFAULT;

  parentCreate?.addEventListener('click', () => {
    if (!current || !parentFilter) return;
    const title = cleanTitle(parentFilter.value);
    if (!title) return;
    const prior = current.parent;
    parentFilter.value = '';
    const chosen = (parentKind?.value ?? CONTAINER_DEFAULT) as NodeKind;
    void run(ctx => createParentEvents(ctx, current!.id, title, prior, chosen),
      `Made “${title}” and put this under it.`);
  });
  btn('#detail-make-project')?.addEventListener('click', () => {
    if (!current) return;
    void run(ctx => makeContainerEvents(ctx, current!.id, current!.kind),
      'It can hold other things now.');
  });
  btn('#detail-kind-set')?.addEventListener('click', () => {
    if (!current || !kindSel) return;
    const chosen = kindSel.value as NodeKind;
    // SAYS SO WHEN NOTHING CHANGED, rather than reporting a change it did not
    // make. The intent writes no event for a no-op; a confirmation over an empty
    // write is the app claiming something happened, which is the family of
    // defect the whole sixth cold read was about.
    if (chosen === current.kind) {
      say(`Already ${CONTAINER_ORDER.find(([k]) => k === chosen)?.[1] ?? 'that'}.`);
      return;
    }
    const words = CONTAINER_ORDER.find(([k]) => k === chosen)?.[1] ?? 'a container';
    void run(ctx => changeContainerKindEvents(ctx, current!.id, current!.kind, chosen),
      `Now ${words}.`);
  });

  btn('#detail-person-set')?.addEventListener('click', () => {
    if (!PERSON || !RELATION || !current) return;
    const name = PERSON.value.trim();
    if (!name) { say('A name first — or leave it, nobody has to be named.'); return; }
    const relation = RELATION.value;
    const st = session.state();
    // Match an existing person by name before minting a second node for the same
    // human. Case-insensitive, because "sam" and "Sam" are one person and a
    // duplicate here would split what you are owed across two rows for ever.
    const existing = peopleNodes(st).find(p => (p.title || '').toLowerCase() === name.toLowerCase());
    PERSON.value = '';
    void run(ctx => {
      const id = existing?.id ?? ctx.id();
      return linkPersonEvents(ctx, current!.id, id, relation, {
        ...(existing ? {} : { createNamed: name }),
        openWaiting: relation === 'waiting-on',
        forWhat: current!.title,
      });
    }, `With ${name}.`);
  });

  // WHERE THIS CAN BE DONE (2.2.0, ADR-0092). The person input's shape, minus
  // the relation — a context has one meaning, so there is nothing to choose.
  btn('#detail-context-set')?.addEventListener('click', () => {
    const input = q<HTMLInputElement>('#detail-context');
    if (!input || !current) return;
    // COMMAS SEPARATE THEM (3.8.0) — see `splitNames`. The placeholder has
    // invited a list since 2.2.0 and the app used to take the whole string as
    // one label.
    const names = splitNames(input.value);
    if (names.length === 0) { say('A place first — or leave it, and it can be done anywhere.'); return; }
    input.value = '';
    void run(ctx => {
      const st = session.state();
      const out = [];
      for (const name of names) {
        // Match by name before minting a second node for the same place, exactly
        // as the person input does: "at home" and "At home" are one place, and a
        // duplicate would split the filter in two for ever. `splitNames` has
        // already collapsed the same two spellings WITHIN this one entry, so no
        // piece here can collide with another piece of it.
        const existing = allContexts(st).find(c => (c.title || '').toLowerCase() === name.toLowerCase());
        const id = existing?.id ?? ctx.id();
        out.push(...attachContextEvents(ctx, current!.id, id, existing ? {} : { createNamed: name }));
      }
      return out;
    }, `Can be done ${andWords(names)}.`);
  });

  // WHO THIS IS FOR (2.6.0, ADR-0096). The place input's shape exactly.
  btn('#detail-role-set')?.addEventListener('click', () => {
    const input = q<HTMLInputElement>('#detail-role');
    if (!input || !current) return;
    // Commas separate them, on this axis too — one shape, one behavior.
    const names = splitNames(input.value);
    if (names.length === 0) { say('A name first — or leave it, and it belongs to no one in particular.'); return; }
    input.value = '';
    void run(ctx => {
      const st = session.state();
      const out = [];
      for (const name of names) {
        // Match by name before minting a second node for the same identity,
        // exactly as places and people do: "parent" and "Parent" are one role,
        // and a duplicate would split the readout in two for ever.
        const existing = allRoles(st).find(r => (r.title || '').toLowerCase() === name.toLowerCase());
        const id = existing?.id ?? ctx.id();
        out.push(...attachRoleEvents(ctx, current!.id, id, existing ? {} : { createNamed: name }));
      }
      return out;
    }, `Part of ${andWords(names)}.`);
  });

  btn('#detail-track')?.addEventListener('click', () => {
    void run(ctx => setTrackRoleEvents(ctx, current!.id, 'track'),
      'You are carrying this, not doing it. Nothing under it will be offered as your next step.');
  });
  btn('#detail-untrack')?.addEventListener('click', () => {
    void run(ctx => setTrackRoleEvents(ctx, current!.id, 'execute'), 'Back to yours to do.');
  });
  btn('#detail-suspense-set')?.addEventListener('click', () => {
    const key = q<HTMLInputElement>('#detail-suspense')?.value ?? '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) { say('Pick a date first.'); return; }
    void run(ctx => setSuspenseEvents(ctx, current!.id, key), `Answer owed by ${dayPicked(key)}.`);
  });

  btn('#detail-save-set')?.addEventListener('click', () => {
    if (!current) return;
    // An empty box means "not said", not zero. `Number('')` is 0, which would
    // silently record that a thing costs nothing.
    const read = (sel: string): number | null => {
      const el = q<HTMLInputElement>(sel);
      const raw = (el?.value ?? '').trim();
      if (raw === '') return null;
      const num = Number(raw);
      return Number.isFinite(num) && num >= 0 ? num : null;
    };
    void run(ctx => setSaveForEvents(ctx, current!.id, read('#detail-save-target'), read('#detail-save-saved')),
      'Noted.');
  });

  btn('#detail-close')?.addEventListener('click', () => DLG.close());

  // --- what happened to this (1.4.0) ----------------------------------------
  // The log filtered to this one node, in the shared plain words, cures
  // indented under their cause. Built on first open of the disclosure and
  // rebuilt after each commit while it stays open — never before: reading the
  // whole log for a closed <details> would be the coverage-list bug again.
  const historyEl = q<HTMLDetailsElement>('#detail-history');
  const historyLines = q<HTMLElement>('#detail-history-lines');
  const buildHistory = (id: string): void => {
    if (!historyLines) return;
    void session.store.all().then(all => {
      // The sheet may have moved on while the read was in flight.
      if (!current || current.id !== id) return;
      const st = session.state();
      const titleOf = (x: string): string | null => st.nodes.get(x)?.title || null;
      historyLines.replaceChildren(...all.filter(e => e.node === id).map(e => {
        const li = document.createElement('li');
        li.className = isCure(e) ? 'log-line log-cure' : 'log-line';
        const day = new Date(e.at).toLocaleDateString(undefined, {
          weekday: 'short', day: 'numeric', month: 'short', timeZone: session.zone,
        });
        li.textContent = `${eventWords(e, session.zone, titleOf)} — ${day}.`;
        return li;
      }));
    });
  };
  historyEl?.addEventListener('toggle', () => {
    if (historyEl.open && current) buildHistory(current.id);
  });

  /** Show a node in this sheet. Named so the sheet's own rows can walk to
   *  another node — a person, a folded twin — without leaving it (1.12.0). */
  function showNode(node: NodeState): void {
    // The picks always open in their ADDING words (3.8.0). Correcting a label is
    // a rare errand somebody goes looking for; leaving the sheet in that state
    // means the next item you open offers to delete your places, which is a
    // destructive control nobody asked for arriving unannounced.
    fixingPlaces = false;
    fixingRoles = false;
    // A different item starts with its history folded away — leaving the
    // last item's lines under a fresh title would be the sheet lying.
    if (historyEl && current?.id !== node.id) {
      historyEl.open = false;
      historyLines?.replaceChildren();
    }
    render(node);
    LIVE.textContent = '';
    if (!DLG.open) DLG.showModal();
    // AND THE FOCUS GOES WHERE THIS APP CHOSE (3.8.2). See `focusSheetTitle`.
    // This sheet is the longest one in the app and it called `showModal()` and
    // set nothing, so which element it opened on was the engine's decision —
    // and on WebKit that means the first tabbable element, with the scroller
    // dragged to wherever that is. It runs after `render`, because render is
    // what fills `#detail-title`.
    focusSheetTitle(DLG);
  }

  /**
   * OPEN, AND SHOW THE THING THE TRIP WAS FOR (3.23.12).
   *
   * `1.39.1` established this twice already — a role's sheet and a person's
   * sheet are almost entirely the folded half, so a navigation that lands on
   * them shut puts the point of the trip out of sight, and both of those
   * handlers call `setRest(true)` themselves. The People screen is the third,
   * and it was the one that broke: its rows say *Nobody named yet*, tapping
   * one opened a sheet with no name field on it, and the control that answers
   * the question was nine sections down behind *More about this*.
   *
   * SCROLLED, NEVER FOCUSED. The sheet focuses its heading on open — the house
   * rule for a navigated view — and stealing that would announce a text field
   * instead of the thing that was opened. Unfolding and bringing the group into
   * view is what a reader needs; where the caret goes is still theirs.
   */
  const openRevealing = (n: NodeState, opts?: DetailOpen): void => {
    showNode(n);
    if (opts?.reveal !== 'person') return;
    setRest(true);
    q('#detail-person-link-group')?.scrollIntoView({ block: 'nearest' });
  };
  return { open: openRevealing };
}
